import React, { useState, useEffect, useRef } from 'react';
import { 
  Heart, MessageCircle, User, Plus, Search, Sparkles, X, Settings, BadgeCheck, 
  ChevronLeft, MapPin, Send, LogOut, Home, UploadCloud, Hash, Wand2, Loader2, 
  MessageSquare, CheckCircle2, ChevronRight, LayoutDashboard, ShieldCheck, 
  PlaySquare, Flag, ShieldPlus, UserCheck, Eye, EyeOff, Bookmark, Lock, Ban, 
  UserMinus, UserPlus, Megaphone, BarChart3, Users, FileText, CalendarDays, Pin,
  CheckSquare, Square, FolderOpen, Pencil, Trash2, Construction, Power
} from 'lucide-react';

import { isContentDeleted, getAuditActionLabel, AUDIT_ACTION_LABELS, formatAuditBanLabel, formatAdminGrantLabel, isAdminModerationLog } from './compliance.js';
import {
  buildMaintenanceNotice,
  formatMaintenanceWindow,
  isMaintenanceActive,
  toDatetimeLocalValue,
  fromDatetimeLocalValue
} from './siteMaintenance.js';
import {
  defaultPublishFormData,
  suggestDraftName,
  getDraftPreviewText,
  getPublishDraft,
  upsertPublishDraft,
  deletePublishDrafts,
  validateDraftForPublish
} from './publishDrafts.js';
import { LocationPicker, LocationConsentModal } from './LocationPicker.js';
import { buildWorkInteractionSummary } from './workInteractions.js';
import { buildLocationProfile, setAutoLocationConsent } from './geoUtils.js';
import {
  CYBERSECURITY_LAW_COMPLIANCE_NOTICE,
  CYBERSECURITY_LAW_FOOTER_LABEL,
  CYBERSECURITY_LAW_PRIVACY_PARAGRAPHS,
  CYBERSECURITY_LAW_AGREEMENT_PARAGRAPHS,
  CYBERSECURITY_LAW_CONVENTION_PARAGRAPHS,
  isCybersecurityLawHighlight
} from './cybersecurityCompliance.js';

export const DEFAULT_COVER = "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?auto=format&fit=crop&w=1200&q=80";
export const DEFAULT_AVATAR = "https://api.dicebear.com/7.x/avataaars/svg?seed=default";
export const OFFICIAL_ADMIN_PHONE = "18100136698";

export const isSuperAdminProfile = (profile) =>
  profile?.role === 'super_admin' || String(profile?.phone || '') === OFFICIAL_ADMIN_PHONE;

export const isAdminProfile = (profile) =>
  isSuperAdminProfile(profile) || profile?.role === 'admin';

export const formatBanDaysLabel = (days) => {
  if (days === -1) return '永久';
  if (days === 1) return '1 天';
  if (days === 7) return '7 天';
  if (days === 30) return '30 天';
  return `${days} 天`;
};

export const UX_HINT_KEYS = {
  publishFeatures: 'yugui_publish_feature_hint_seen',
  profileBatch: 'yugui_profile_batch_hint_seen'
};

export const hasUxHintSeen = (key) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

export const markUxHintSeen = (key) => {
  try {
    localStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
};
export const reportReasons = ["血腥暴力", "虐待动物", "虚假骗局", "商业繁殖", "侵权盗用", "色情低俗", "骚扰辱骂", "垃圾广告", "违法信息", "其他违规"];

/** 封面是否为视频（领养卡片、发现页等共用） */
export const isPostVideoMedia = (item) =>
  !!item?.isVideo ||
  (typeof item?.img === 'string' &&
    (item.img.startsWith('data:video') || /\.(mp4|webm|ogg|mov|ogv)(\?|#|$)/i.test(item.img)));

/** 作品媒体地址（相对 /api/media 转绝对 URL，避免重登后视频不播） */
export const resolvePostMediaUrl = (item) => {
  const src = item?.img || item?.image || item?.media || item?.cover || '';
  if (!src || typeof src !== 'string') return '';
  if (src.startsWith('data:') || src.startsWith('blob:') || /^https?:\/\//i.test(src)) return src;
  if (src.startsWith('/') && typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${src}`;
  }
  return src;
};

const buildYuguiApiUrl = (suffixPath) => {
  const base = (
    typeof process !== 'undefined' && process.env.REACT_APP_API_BASE
      ? String(process.env.REACT_APP_API_BASE).trim()
      : '/api'
  ).replace(/\/$/, '');
  const suf = suffixPath.startsWith('/') ? suffixPath : `/${suffixPath}`;
  if (base.startsWith('http')) return `${base}${suf}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    const b = base.startsWith('/') ? base : `/${base}`;
    return `${window.location.origin}${b}${suf}`;
  }
  return `${base}${suf}`;
};

/** 公安联网备案（全国互联网安全管理服务平台） */
export const PSB_RECORD_CODE = '33020302002115';
export const PSB_RECORD_LABEL = '浙公网安备33020302002115号';
export const PSB_RECORD_URL = `http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=${PSB_RECORD_CODE}`;
export const PSB_EMBLEM_SRC = `${process.env.PUBLIC_URL || ''}/psb-emblem.png`;

/** 网站底部备案信息（国徽图标 + 公安备案号，链接至全国互联网安全管理服务平台） */
export const SiteLegalFooter = ({ className = '' }) => (
  <footer
    className={`shrink-0 border-t border-white/5 bg-stone-950/95 backdrop-blur-sm px-4 py-2.5 ${className}`}
    aria-label="网站备案信息"
  >
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] md:text-[11px] text-white/40">
      <span className="text-white/30">© 予归 · nbyugui.cn</span>
      <span className="text-white/25 hidden sm:inline" title={CYBERSECURITY_LAW_COMPLIANCE_NOTICE}>
        {CYBERSECURITY_LAW_FOOTER_LABEL}
      </span>
      <a
        href={PSB_RECORD_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 hover:text-white/65 transition-colors"
        title="全国互联网安全管理服务平台"
      >
        <img
          src={PSB_EMBLEM_SRC}
          alt=""
          className="w-[14px] h-[14px] object-contain shrink-0"
          width={14}
          height={14}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'https://beian.mps.gov.cn/webImage/icon/ga_icon.png';
          }}
        />
        <span>{PSB_RECORD_LABEL}</span>
      </a>
    </div>
  </footer>
);

export const privacyPolicyContent = [
  "【生效说明】本《隐私政策》适用于「予归」（nbyugui.cn）提供的全部服务。请您在使用前仔细阅读；注册、登录或使用本平台，即表示您已理解并同意本政策。",
  CYBERSECURITY_LAW_COMPLIANCE_NOTICE,
  "【1. 我们收集的信息】为实现账号注册、登录、内容发布、同城领养匹配与平台安全，我们可能依法收集：手机号码、账号昵称、头像、简介、城市/地区信息（非精确门牌地址）、您主动发布的图文/视频内容、设备基础信息（浏览器类型、系统版本）、IP 属地、操作日志（登录、发布、删除、举报、点赞、收藏等）。",
  "【2. 位置信息】仅在您勾选同意或在设置中开启后，我们才会获取城市级定位或您手动选择的城市，用于同城/附近领养推荐与展示。您可随时在「账号设置」中关闭自动定位或改选手动城市。拒绝授权不影响浏览、登录及其他非定位功能。",
  "【3. 信息使用目的】我们使用上述信息用于：提供与维护服务、账号安全与身份核验、内容展示与互动、违规内容识别与处置、处理举报与纠纷、改进产品体验、履行法律法规规定的义务。",
  "【4. 存储与安全】您的数据存储于符合安全要求的服务器与云数据库中，采用访问控制、传输加密等合理措施。我们不出售您的个人信息。除法律法规要求、监管机关依法调取、或经您明确同意外，不向无关第三方提供您的个人信息。",
  "【5. 内容可见性与删除】您可将作品设为仅自己可见，也可删除自己发布的内容；删除后前台不再公开展示。为处理举报、审计、维权及配合依法调查，我们可能在必要范围内保留摘要、操作记录与备份，并在法定保存期限届满后删除或匿名化处理。",
  "【6. 第三方服务】地图定位、图片生成等能力可能由第三方提供；我们仅向其传递实现功能所必需的信息，并要求其遵守适用的隐私与安全要求。",
  "【7. 您的权利】您有权查询、更正、删除个人信息，撤回定位等授权，注销账号，以及就个人信息处理向我们提出疑问或投诉。请通过平台「官方反馈」联系我们，我们将在合理期限内答复。",
  "【8. 未成年人保护】若您未满 18 周岁，请在监护人同意与指导下使用本平台。",
  "【9. 政策更新】我们可能适时更新本政策，并在平台公布。重大变更将通过页面提示等方式告知；您继续使用服务即视为接受更新后的政策。",
  ...CYBERSECURITY_LAW_PRIVACY_PARAGRAPHS,
  "【14. 联系我们】运营主体：予归平台。网站：https://nbyugui.cn 。反馈入口：登录后「官方反馈」。"
];

export const userAgreementContent = [
  "【生效说明】本《用户协议》是您与「予归」平台之间关于使用本平台服务的法律协议。请仔细阅读；注册、登录或使用即表示您接受本协议全部条款。",
  CYBERSECURITY_LAW_COMPLIANCE_NOTICE,
  "【1. 平台性质】「予归」是公益领养与宠物信息分享平台，为用户提供信息发布、浏览、交流与撮合服务。平台不是宠物买卖、有偿转让或非法交易场所，不对线下交易作担保。",
  "【2. 账号注册与使用】您应使用真实、有效的手机号注册，妥善保管密码，对账号下的一切行为负责。不得冒用他人身份、批量注册、买卖账号或利用账号从事违法活动。",
  "【3. 用户内容】您对自己发布的内容负责，应保证不侵犯他人知识产权、肖像权、隐私权等合法权益。您授予平台非独占、免费的展示许可，以便在平台内传播您主动公开的内容。",
  "【4. 禁止行为】严禁发布或从事：虐待/遗弃动物、商业后院繁殖、虚假领养、诈骗、色情低俗、暴力恐怖、政治违法、侵权盗版、骚扰辱骂、宠物买卖或变相收费领养等行为。",
  "【5. 领养与线下交接】领养/送养双方应线下核实身份，建议签订书面协议，明确疫苗、绝育、回访与责任划分。平台不对宠物健康、品种、身份真实性作担保；线下纠纷由当事人自行协商或通过法律途径解决。",
  "【6. 互动功能】点赞、收藏、评论、关注、私信等功能仅供正常交流使用，不得用于骚扰、广告刷屏或违法目的。",
  "【7. 举报与处置】用户可通过举报功能反馈违规内容。平台有权删除、下架、限制可见范围、限制功能或封禁账号，并依法留存必要记录、配合调查。",
  "【8. 知识产权】平台界面、标识、文案等受法律保护，未经授权不得复制、爬取或用于商业用途。",
  "【9. 服务变更与中断】我们可能因维护、升级、不可抗力等原因暂停或中断部分服务，并将尽力提前告知。",
  "【10. 协议变更】我们有权更新本协议并在平台公布。若您不同意变更，应停止使用；继续使用视为接受。",
  ...CYBERSECURITY_LAW_AGREEMENT_PARAGRAPHS,
  "【13. 平台公约】您须同时遵守《予归平台公约》。本协议与公约不一致的，以更严格保护公益领养环境与用户安全的条款为准。",
  "【14. 争议解决】因本协议产生的争议，双方应友好协商；协商不成的，提交平台运营者所在地有管辖权的人民法院诉讼解决（另有强制性法律规定除外）。"
];

/** 平台公约版本号；更新文案后递增，已登录用户需重新确认 */
export const PLATFORM_CONVENTION_VERSION = '1.4';

export const platformConventionContent = [
  "【1. 平台定位】「予归」是公益领养与宠物信息分享平台，不是宠物买卖、有偿转让或非法交易场所。",
  CYBERSECURITY_LAW_COMPLIANCE_NOTICE,
  "【2. 禁止变相交易】禁止以「领养」「免费送」为名变相收费、捆绑消费、买卖活体或从事商业繁殖。",
  "【3. 禁止违规内容】禁止发布虐待、遗弃、商业繁殖（后院繁殖）、虚假宠物信息、盗用他人图片或视频、骚扰辱骂等内容。",
  "【4. 线下核实与协议】领养/送养双方应线下核实身份，建议签订书面领养协议，明确疫苗、绝育、回访等责任。",
  "【5. 平台免责】平台不对宠物健康状况、品种纯度、对方真实身份作任何担保；线下交接及相关风险由当事人自行承担。",
  "【6. 举报与处置】发现违规内容或行为，请使用举报功能；平台有权下架内容、限制功能、封禁账号，并依法留存相关记录、配合主管部门调查。",
  ...CYBERSECURITY_LAW_CONVENTION_PARAGRAPHS,
  "【10. 每次登录确认】您每次登录账号时须阅读并勾选确认本公约，表示您已知悉并承诺遵守公益领养相关规范。",
  "【11. 违规后果】违反本公约或法律法规的，平台可依据《用户协议》暂停或终止向您提供服务，并依法配合调查。"
];

export const CONVENTION_STORAGE_KEY = 'yugui_convention_accepted';
export const CONVENTION_SESSION_KEY = 'yugui_convention_session';

export const clearConventionSessionAcceptance = () => {
  try {
    sessionStorage.removeItem(CONVENTION_SESSION_KEY);
  } catch (_) {}
};

export const getConventionAcceptance = (phone) => {
  if (!phone) return null;
  try {
    const raw = sessionStorage.getItem(CONVENTION_SESSION_KEY);
    const data = raw ? JSON.parse(raw) : null;
    if (!data || String(data.phone) !== String(phone)) return null;
    return data;
  } catch {
    return null;
  }
};

export const saveConventionAcceptance = (phone) => {
  if (!phone) return;
  const payload = {
    phone: String(phone),
    version: PLATFORM_CONVENTION_VERSION,
    acceptedAt: Date.now()
  };
  try {
    sessionStorage.setItem(CONVENTION_SESSION_KEY, JSON.stringify(payload));
  } catch (_) {}
  localStorage.setItem(CONVENTION_STORAGE_KEY, JSON.stringify(payload));
};

export const needsConventionAcceptance = (phone) => {
  const acc = getConventionAcceptance(phone);
  return !acc || acc.version !== PLATFORM_CONVENTION_VERSION;
};

export const ConfirmModal = ({ title, desc, onConfirm, onCancel, confirmText = "确认", cancelText = "取消", isDanger = true }) => (
    <div className="fixed inset-0 z-[4000] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in">
    <div className="bg-zinc-900 border border-white/10 rounded-[2rem] w-full max-w-sm p-8 text-center shadow-2xl">
      <h3 className={`text-xl font-black mb-3 ${isDanger ? 'text-red-500' : 'text-amber-500'}`}>{title}</h3>
      <p className="text-sm text-white/60 mb-8 leading-relaxed">{desc}</p>
      <div className="flex space-x-4">
         <button onClick={onCancel} className="flex-1 py-3 bg-white/5 text-white/60 font-bold rounded-xl hover:text-white transition-colors">{cancelText}</button>
         <button onClick={() => { onConfirm(); onCancel(); }} className={`flex-1 py-3 font-black rounded-xl transition-colors ${isDanger ? 'bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white' : 'bg-amber-500 text-black hover:bg-amber-400'}`}>{confirmText}</button>
      </div>
    </div>
  </div>
);

export const LegalDocument = ({ title, content, onClose }) => (
  <div className="fixed inset-0 z-[6500] bg-stone-950 flex flex-col p-8 animate-slide-up">
    <header className="pt-10 pb-6 flex items-center border-b border-white/10">
      <button onClick={onClose} className="p-2 -ml-2"><ChevronLeft size={28}/></button>
      <h2 className="flex-1 text-center font-black text-sm text-amber-500 uppercase tracking-widest">{title}</h2>
    </header>
    <div className="flex-1 overflow-y-auto no-scrollbar py-6 space-y-6 text-white/80 text-xs leading-relaxed">
      {content.map((para, idx) => (
         <div key={idx} className="bg-white/5 p-6 rounded-2xl border border-white/5">
           <p className={isCybersecurityLawHighlight(para) || para.includes("免责声明") || para.includes("内容隐私自主权") || para.includes("禁止") || para.includes("平台定位") ? "text-amber-500 font-bold" : ""}>{para}</p>
         </div>
      ))}
    </div>
  </div>
);

/** 登录/注册成功后须确认的平台公约（须勾选后才能进入） */
export const PlatformConventionAcceptModal = ({ onAccept }) => {
  const [checked, setChecked] = useState(false);

  return (
    <div
      className="fixed inset-0 z-[7000] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
      style={{ background: 'rgba(0,0,0,0.88)', zIndex: 7000 }}
    >
      <div
        className="bg-zinc-900 border border-amber-500/30 rounded-[2rem] w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl shadow-amber-500/10 overflow-hidden"
        style={{ background: '#18181b', color: '#fff', maxHeight: '90vh', width: '100%', maxWidth: '32rem', borderRadius: '1.5rem' }}
      >
        <header className="px-8 pt-8 pb-4 border-b border-white/10 text-center shrink-0">
          <ShieldCheck size={40} className="text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-black text-amber-500">予归平台公约</h2>
          <p className="text-[11px] text-white/40 mt-2">每次登录须阅读并勾选确认，共同维护公益领养环境</p>
        </header>
        <div className="flex-1 overflow-y-auto no-scrollbar px-8 py-6 space-y-4 text-white/80 text-xs leading-relaxed">
          {platformConventionContent.map((para, idx) => (
            <div key={idx} className="bg-white/5 p-4 rounded-xl border border-white/5">
              <p className={isCybersecurityLawHighlight(para) || para.includes("禁止") || para.includes("平台定位") ? "text-amber-400 font-bold" : ""}>{para}</p>
            </div>
          ))}
        </div>
        <footer className="px-8 py-6 border-t border-white/10 shrink-0 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => setChecked(!checked)}
              className="mt-1 accent-amber-500"
            />
            <span className="text-xs text-white/70 leading-relaxed">
              我已阅读并承诺遵守《予归平台公约》《隐私政策》《用户协议》，知晓本平台不是宠物买卖或非法交易场所。
            </span>
          </label>
          <button
            type="button"
            disabled={!checked}
            onClick={onAccept}
            className={`w-full py-4 rounded-xl font-black transition-all ${
              checked
                ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/20 active:scale-[0.98]'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
          >
            确认并进入平台
          </button>
        </footer>
      </div>
    </div>
  );
};

export const DEV_SMS_CODE = '123456';

export const AuthModal = ({ initialMode, onAuth, onClose, onShowPrivacy, onShowAgreement, onShowConvention, showToast, adminLoginOnly = false }) => {
    const [mode, setMode] = useState(adminLoginOnly ? 'login' : (initialMode || 'login')); 
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [isShaking, setIsShaking] = useState(false);
    const [rememberPwd, setRememberPwd] = useState(false);
    const [timer, setTimer] = useState(0);
    const [allowLocation, setAllowLocation] = useState(false);
    const [locationProfile, setLocationProfile] = useState(buildLocationProfile(null));
    const [showLocationConsent, setShowLocationConsent] = useState(false);

    useEffect(() => {
      if (adminLoginOnly) {
        setMode('login');
        setRememberPwd(false);
        return;
      }
      const saved = localStorage.getItem('yugui_saved_account');
      if (saved) {
        const { phone: savedPhone, pwd: savedPwd } = JSON.parse(saved);
        setPhone(savedPhone);
        setPassword(savedPwd);
        setRememberPwd(true);
      }
    }, [adminLoginOnly]);

    const isValidPhone = (value) => /^1[3-9]\d{9}$/.test(String(value || ''));

    const handleSendCode = () => {
      if (!isValidPhone(phone)) return showToast('请输入真实有效的11位手机号');
      setTimer(60);
      const int = setInterval(() => setTimer((p) => (p <= 1 ? (clearInterval(int), 0) : p - 1)), 1000);
      showToast(`验证码已发送（内测固定码 ${DEV_SMS_CODE}，正式运营后将发到手机）`);
    };

    const finishAuth = (locProfile = locationProfile) => {
      if (!adminLoginOnly && rememberPwd && mode === 'login') {
        localStorage.setItem('yugui_saved_account', JSON.stringify({ phone, pwd: password }));
      } else if (!rememberPwd) {
        localStorage.removeItem('yugui_saved_account');
      }
      onAuth(
        phone,
        mode,
        password,
        code,
        'password',
        setMode,
        locProfile,
        !!locProfile?.locationAgreed
      );
    };

    const submit = () => {
      if (!agreed && mode !== 'forgot') {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
        return;
      }
      if (!isValidPhone(phone)) return showToast('请输入真实有效的11位手机号');
      if (mode === 'register' || mode === 'forgot') {
        if (!password || password.length < 6) {
          return showToast(mode === 'register' ? '密码至少6位' : '新密码至少6位');
        }
        if (password !== confirmPwd) return showToast('两次输入的密码不一致！');
      }
      if (mode === 'forgot' && !code) return showToast('请输入验证码');
      if ((mode === 'register' || mode === 'login') && allowLocation) {
        setShowLocationConsent(true);
        return;
      }
      finishAuth();
    };

    return (
      <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-4 animate-fade-in">
        <div className={`bg-zinc-900 border border-white/10 rounded-[1.5rem] sm:rounded-[2rem] w-full max-w-md max-h-[92vh] overflow-y-auto no-scrollbar p-6 sm:p-10 shadow-2xl relative transition-all ${isShaking ? 'animate-shake border-red-500/50 shadow-red-500/20' : ''}`}>
          <button onClick={onClose} className="absolute top-6 right-6 p-2 text-white/40 hover:text-white bg-white/5 rounded-full transition-colors hover:bg-white/10"><X size={24}/></button>
          
          <h2 className="text-3xl font-black mb-2 text-amber-500 tracking-wider text-center">
            {adminLoginOnly ? '管理员登录' : mode === 'login' ? '欢迎回来' : mode === 'register' ? '加入予归' : '重置密码'}
          </h2>
          <p className="text-xs text-white/40 mb-8 tracking-widest text-center">
            {adminLoginOnly ? '维护期间仅管理员/超级管理员可验证进入' : '让宠物变得更好，给生命一个家'}
          </p>
          
          <div className="space-y-4 mb-8">
            <div className="bg-white/5 rounded-xl p-4 flex items-center focus-within:border-amber-500/50 transition-colors border border-transparent">
              <span className="text-white/40 font-bold mr-3 border-r border-white/10 pr-3">+86</span>
              <input type="tel" placeholder="请输入11位手机号" value={phone} onChange={e=>setPhone(e.target.value.replace(/\D/g, '').slice(0,11))} className="bg-transparent outline-none w-full text-white font-medium" />
            </div>
            <p className="text-[10px] text-white/35 px-2 leading-relaxed">
              {adminLoginOnly
                ? '请使用管理员或超级管理员账号重新输入密码验证，本地记住的密码不会自动填入。'
                : `一个手机号对应一个账号。已注册账号请用密码登录；验证码用于忘记密码等安全操作（内测固定 ${DEV_SMS_CODE}）。`}
            </p>

            {mode === 'forgot' && (
              <div className="bg-white/5 rounded-xl p-4 flex items-center focus-within:border-amber-500/50 transition-colors border border-transparent animate-fade-in">
                <input
                  type="text"
                  placeholder="6位验证码"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="bg-transparent outline-none w-full text-white font-medium"
                />
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={timer > 0 || !isValidPhone(phone)}
                  className="text-xs font-bold text-amber-500 disabled:text-white/20 whitespace-nowrap ml-2"
                >
                  {timer > 0 ? `${timer}s后重发` : '获取验证码'}
                </button>
              </div>
            )}

            {(mode === 'register' || mode === 'forgot' || mode === 'login') && (
               <div className="bg-white/5 rounded-xl p-4 flex items-center focus-within:border-amber-500/50 transition-colors border border-transparent animate-fade-in relative">
                 <input
                   type={showPwd ? 'text' : 'password'}
                   placeholder={
                     mode === 'register'
                       ? '设置密码(至少6位)'
                       : mode === 'forgot'
                         ? '设置新密码(至少6位)'
                         : '请输入密码'
                   }
                   value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   className="bg-transparent outline-none w-full text-white font-medium pr-10"
                 />
                 <button onClick={() => setShowPwd(!showPwd)} className="absolute right-4 text-white/40 hover:text-white transition-colors">
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                 </button>
               </div>
            )}

            {(mode === 'register' || mode === 'forgot') && (
              <div className="bg-white/5 rounded-xl p-4 flex items-center focus-within:border-amber-500/50 transition-colors border border-transparent animate-fade-in relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  placeholder={mode === 'register' ? '再次输入密码' : '再次输入新密码'}
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  className="bg-transparent outline-none w-full text-white font-medium pr-10"
                />
                <button onClick={() => setShowPwd(!showPwd)} className="absolute right-4 text-white/40 hover:text-white transition-colors">
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            )}

            {mode === 'login' && !adminLoginOnly && (
              <div className="flex items-center px-2">
                <input type="checkbox" id="remember" checked={rememberPwd} onChange={()=>setRememberPwd(!rememberPwd)} className="accent-amber-500 mr-2" />
                <label htmlFor="remember" className="text-xs text-white/40">记住密码</label>
              </div>
            )}

            {(mode === 'login' || mode === 'register') && !adminLoginOnly && (
              <div className="flex items-start gap-2 p-3 bg-white/5 rounded-xl border border-white/5">
                <input
                  type="checkbox"
                  id="allowLocation"
                  checked={allowLocation}
                  onChange={() => setAllowLocation(!allowLocation)}
                  className="mt-0.5 accent-amber-500 cursor-pointer shrink-0"
                />
                <label htmlFor="allowLocation" className="text-[11px] text-white/60 leading-relaxed cursor-pointer select-none">
                  允许获取所在城市，用于同城/附近领养推荐（可在账号设置中随时修改）
                </label>
              </div>
            )}
          </div>
          
          {mode !== 'forgot' && (
            <>
              <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/40 rounded-xl">
                <div className="flex items-start gap-3">
                  <ShieldCheck size={22} className="text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-black text-amber-500 mb-1">予归平台公约</p>
                    <p className="text-[11px] text-white/60 leading-relaxed">
                      本平台是公益领养与信息分享空间，<span className="text-amber-400 font-bold">不是</span>宠物买卖或非法交易场所。注册/登录即表示您知悉相关规范。
                    </p>
                    <button
                      type="button"
                      onClick={() => onShowConvention?.()}
                      className="text-[11px] text-amber-500 font-bold mt-2 hover:underline"
                    >
                      阅读完整《平台公约》→
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-start space-x-2 mb-4 p-3 bg-white/5 rounded-xl border border-white/5">
                <input type="checkbox" id="agree" checked={agreed} onChange={()=>setAgreed(!agreed)} className="mt-1 accent-amber-500 cursor-pointer" />
                <label htmlFor="agree" className="text-[10px] text-white/50 leading-relaxed cursor-pointer select-none">
                  我已阅读并承诺遵守{' '}
                  <span className="text-amber-500 hover:underline font-bold" onClick={(e) => { e.preventDefault(); onShowPrivacy(); }}>《隐私政策》</span>、
                  <span className="text-amber-500 hover:underline font-bold" onClick={(e) => { e.preventDefault(); onShowAgreement(); }}>《用户协议》</span>与
                  <span className="text-amber-500 hover:underline font-bold" onClick={(e) => { e.preventDefault(); onShowConvention?.(); }}>《平台公约》</span>
                  。予归平台作为网络运营者，依法履行《网络安全法》规定的个人信息保护与网络安全义务。
                </label>
              </div>
            </>
          )}
          
          <button
            onClick={submit}
            className={`w-full py-4 rounded-xl font-black transition-all ${agreed || mode === 'forgot' ? 'bg-amber-500 text-black shadow-xl shadow-amber-500/20 active:scale-95' : 'bg-white/10 text-white/30'}`}
          >
            {adminLoginOnly ? '验证并进入' : mode === 'login' ? '安全登录平台' : mode === 'register' ? '完成注册并登录' : '确认重置密码'}
          </button>

          {!adminLoginOnly && (
          <div className="flex justify-between items-center mt-6 pt-6 border-t border-white/5">
             {mode === 'login' ? (
               <>
                 <button
                   type="button"
                   onClick={() => setMode('forgot')}
                   className="text-xs text-white/30 hover:text-white transition-colors"
                 >
                   忘记密码？
                 </button>
                 <button onClick={() => setMode('register')} className="text-xs text-amber-500 font-bold hover:text-amber-400 transition-colors">注册新账号</button>
               </>
             ) : (
               <button onClick={() => setMode('login')} className="text-xs text-white/30 hover:text-white mx-auto transition-colors">返回登录</button>
             )}
          </div>
          )}
        </div>
        {showLocationConsent && (mode === 'register' || mode === 'login') && (
          <LocationConsentModal
            showToast={showToast}
            onAllow={(profile) => {
              setLocationProfile(profile);
              setShowLocationConsent(false);
              finishAuth(profile);
            }}
            onManual={(profile) => {
              setLocationProfile(profile);
              setShowLocationConsent(false);
              finishAuth(profile);
            }}
            onDeny={() => {
              setLocationProfile(buildLocationProfile(null));
              setShowLocationConsent(false);
              finishAuth(buildLocationProfile(null));
            }}
          />
        )}
      </div>
    );
};

export { clearPublishDraftStorage, clearAllPublishDrafts, loadPublishDrafts } from './publishDrafts.js';

export const DraftBoxPanel = ({
  drafts = [],
  batchMode,
  selectedIds = [],
  onToggleBatchMode,
  onToggleSelect,
  onToggleSelectAll,
  onEdit,
  onRename,
  onDeleteDraft,
  onBatchDelete,
  onBatchPublish,
  onCreateDraft,
  showToast
}) => {
  const allSelected = drafts.length > 0 && drafts.every((d) => selectedIds.includes(String(d.id)));

  return (
    <div className="space-y-4 pb-8">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onToggleBatchMode}
          className={`px-4 py-2 rounded-full text-xs font-black border transition-colors ${
            batchMode
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
              : 'bg-white/5 border-white/10 text-white/70 hover:text-white'
          }`}
        >
          {batchMode ? '退出批量' : '批量管理'}
        </button>
        <button
          type="button"
          onClick={onCreateDraft}
          className="px-4 py-2 rounded-full text-xs font-black bg-amber-500 text-black hover:bg-amber-400 transition-colors"
        >
          新建草稿
        </button>
        {batchMode && (
          <>
            <button
              type="button"
              onClick={onToggleSelectAll}
              className="px-4 py-2 rounded-full text-xs font-bold bg-white/5 border border-white/10 text-white/60 hover:text-white"
            >
              {allSelected ? '取消全选' : '全选'}
            </button>
            <button
              type="button"
              disabled={!selectedIds.length}
              onClick={onBatchPublish}
              className="px-4 py-2 rounded-full text-xs font-black bg-amber-500 text-black disabled:opacity-30"
            >
              批量发布 ({selectedIds.length})
            </button>
            <button
              type="button"
              disabled={!selectedIds.length}
              onClick={onBatchDelete}
              className="px-4 py-2 rounded-full text-xs font-black bg-red-500/15 text-red-400 border border-red-500/30 disabled:opacity-30"
            >
              批量删除 ({selectedIds.length})
            </button>
          </>
        )}
      </div>

      {drafts.length === 0 ? (
        <div className="py-16 text-center opacity-40">
          <FolderOpen size={48} className="mx-auto mb-4 text-amber-500/50" />
          <p className="font-black text-lg mb-2">草稿箱是空的</p>
          <p className="text-sm text-white/40 mb-6">发布时可保存草稿，删除作品时也可选择保留到草稿箱</p>
          <button
            type="button"
            onClick={onCreateDraft}
            className="px-6 py-3 rounded-full bg-amber-500 text-black text-sm font-black hover:bg-amber-400"
          >
            写第一条草稿
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {drafts.map((draft) => {
            const fd = draft.formData || {};
            const typeLabel = fd.type === 'adopt' ? '领养' : '日常';
            const selected = selectedIds.includes(String(draft.id));
            const publishErr = validateDraftForPublish(fd);
            return (
              <div
                key={draft.id}
                className={`bg-zinc-900 border rounded-2xl p-4 flex gap-3 transition-colors ${
                  selected ? 'border-amber-500/50 bg-amber-500/5' : 'border-white/5 hover:border-white/15'
                }`}
              >
                {batchMode && (
                  <button
                    type="button"
                    onClick={() => onToggleSelect(String(draft.id))}
                    className="shrink-0 pt-1 text-white/50 hover:text-amber-400"
                    aria-label={selected ? '取消选择' : '选择草稿'}
                  >
                    {selected ? <CheckSquare size={22} className="text-amber-500" /> : <Square size={22} />}
                  </button>
                )}
                <div className="w-16 h-16 rounded-xl bg-black/40 shrink-0 overflow-hidden border border-white/10">
                  {fd.img ? (
                    <img src={fd.img} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-white/30 font-bold">
                      无封面
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-black text-sm truncate">{draft.name || '未命名草稿'}</p>
                    <span className="text-[10px] font-bold text-amber-500/80 shrink-0">{typeLabel}</span>
                  </div>
                  <p className="text-xs text-white/45 mt-1 line-clamp-2">{getDraftPreviewText(draft)}</p>
                  <p className="text-[10px] text-white/30 mt-1">
                    更新于 {new Date(draft.updatedAt || draft.savedAt || 0).toLocaleString('zh-CN')}
                  </p>
                  {publishErr && (
                    <p className="text-[10px] text-orange-400/90 mt-1">发布前需补充：{publishErr}</p>
                  )}
                  {!batchMode && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => onEdit(draft)}
                        className="px-3 py-1.5 rounded-lg bg-amber-500 text-black text-[11px] font-black hover:bg-amber-400"
                      >
                        编辑
                      </button>
                      <button
                        type="button"
                        onClick={() => onRename(draft)}
                        className="px-3 py-1.5 rounded-lg bg-white/5 text-white/70 text-[11px] font-bold hover:bg-white/10 flex items-center gap-1"
                      >
                        <Pencil size={12} /> 重命名
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!window.confirm(`确定删除草稿「${draft.name}」？`)) return;
                          onDeleteDraft(String(draft.id));
                        }}
                        className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-[11px] font-bold hover:bg-red-500/20 flex items-center gap-1"
                      >
                        <Trash2 size={12} /> 删除
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PublishModal = ({
  onPublish,
  onClose,
  showToast,
  userProfile,
  editingDraftId = null,
  onDraftSaved,
  onDraftDeleted,
  onSaveDraft,
  onDeleteDraft
}) => {
    const defaultFormData = defaultPublishFormData();
    const [formData, setFormData] = useState(defaultFormData);
    const [isPolishing, setIsPolishing] = useState(false);
    const [polishDraft, setPolishDraft] = useState('');
    const [showPolishModal, setShowPolishModal] = useState(false);
    const [smartTags, setSmartTags] = useState(["寻找领养", "日常吸猫", "汪星人", "异宠日常", "求助"]);
    const [adoptConventionAgreed, setAdoptConventionAgreed] = useState(false);
    const [showFirstPublishTip, setShowFirstPublishTip] = useState(false);
    const [activeDraftId, setActiveDraftId] = useState(editingDraftId || null);
    const [activeDraftName, setActiveDraftName] = useState('');
    const [isPublishing, setIsPublishing] = useState(false);
    const [publishStatus, setPublishStatus] = useState('');
    const mediaBlobRef = useRef(null);

    const dismissFirstPublishTip = () => {
      markUxHintSeen(UX_HINT_KEYS.publishFeatures);
      setShowFirstPublishTip(false);
    };

    useEffect(() => {
      if (!hasUxHintSeen(UX_HINT_KEYS.publishFeatures)) {
        setShowFirstPublishTip(true);
      }
    }, []);

    const revokePreviewBlob = (url) => {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url); } catch (_) {}
      }
    };

    useEffect(() => {
      if (!editingDraftId) return;
      const saved = getPublishDraft(editingDraftId);
      if (!saved?.formData) return;
      const fd = saved.formData;
      setFormData({
        ...defaultFormData,
        ...fd,
        type: fd.type === 'adopt' ? 'adopt' : 'post',
        health: fd.health && typeof fd.health === 'object' ? fd.health : defaultFormData.health,
        tags: Array.isArray(fd.tags) ? fd.tags : [],
        rawFile: null,
        img: typeof fd.img === 'string' ? fd.img : '',
        isVideo: !!fd.isVideo
      });
      setActiveDraftId(saved.id);
      setActiveDraftName(saved.name || '');
      if (fd.type === 'adopt') setAdoptConventionAgreed(true);
      showToast?.(`已打开草稿「${saved.name || '未命名'}」`);
    }, [editingDraftId]);

    const resetPublishForm = () => {
      revokePreviewBlob(formData.img);
      if (mediaBlobRef.current) {
        try { URL.revokeObjectURL(mediaBlobRef.current); } catch (_) {}
        mediaBlobRef.current = null;
      }
      setFormData(defaultPublishFormData());
      setAdoptConventionAgreed(false);
      setActiveDraftId(null);
      setActiveDraftName('');
    };

    const handleSaveToDraftBox = async () => {
      const defaultName = activeDraftName || suggestDraftName(formData);
      const name = window.prompt('给草稿起个名字，方便在草稿箱里找到', defaultName);
      if (name === null) return;
      if (!name.trim()) return showToast('草稿名称不能为空');
      const saveDraft = onSaveDraft || upsertPublishDraft;
      const { draft, droppedMedia, isNew } = await saveDraft({
        id: activeDraftId,
        name: name.trim(),
        formData
      });
      setActiveDraftId(draft.id);
      setActiveDraftName(draft.name);
      onDraftSaved?.(draft, { isNew });
      showToast?.(
        droppedMedia
          ? `已保存「${draft.name}」（视频/大图需重新选择后再发）`
          : `已保存到草稿箱：${draft.name}`
      );
    };

    const handleDeleteCurrentDraft = async () => {
      if (!activeDraftId) return;
      if (!window.confirm(`确定从草稿箱删除「${activeDraftName || '该草稿'}」？`)) return;
      if (onDeleteDraft) await onDeleteDraft(activeDraftId);
      else deletePublishDrafts([activeDraftId]);
      onDraftDeleted?.(activeDraftId);
      resetPublishForm();
      showToast?.('草稿已删除');
    };

    useEffect(() => () => {
      if (mediaBlobRef.current) {
        try { URL.revokeObjectURL(mediaBlobRef.current); } catch (_) {}
        mediaBlobRef.current = null;
      }
    }, []);

    useEffect(() => {
       const timer = setTimeout(() => {
           const text = (formData.title + " " + formData.content + " " + formData.petType).toLowerCase();
           let newTags = new Set();
           if (/猫|喵|咪/.test(text)) { newTags.add("喵星人"); newTags.add("日常吸猫"); }
           if (/狗|汪|犬/.test(text)) { newTags.add("修狗狗"); newTags.add("汪星人"); }
           if (/蛙|牛蛙|蟾/.test(text)) { newTags.add("两栖爬宠"); }
           if (/爬|蛇|蜥蜴|龟/.test(text)) { newTags.add("爬行动物"); newTags.add("异宠"); }
           if (formData.type === 'adopt' || /领养|救助/.test(text)) { newTags.add("寻找领养"); newTags.add("给它一个家"); }
           const tagsArr = Array.from(newTags);
           setSmartTags(tagsArr.length > 0 ? tagsArr.slice(0, 8) : ["寻找领养", "日常吸猫", "汪星人", "记录美好"]);
       }, 600); 
       return () => clearTimeout(timer);
    }, [formData.title, formData.content, formData.petType, formData.type]);

    const handleMediaUpload = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 50 * 1024 * 1024) return showToast("请上传50MB以内的视频/照片");
        const isVideoFile = file.type.startsWith('video/');
        if (isVideoFile) {
          if (file.size > 22 * 1024 * 1024) return showToast("视频请控制在22MB以内（建议竖屏、1分钟内）");
          if (mediaBlobRef.current) {
            try { URL.revokeObjectURL(mediaBlobRef.current); } catch (_) {}
            mediaBlobRef.current = null;
          }
          const blobUrl = URL.createObjectURL(file);
          mediaBlobRef.current = blobUrl;
          setFormData({ ...formData, img: blobUrl, rawFile: file, isVideo: true });
          showToast("视频已添加，发布时会自动编码上传");
          e.target.value = '';
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const img = new Image();
          img.src = reader.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const maxWidth = 600;
            const scale = img.width > maxWidth ? maxWidth / img.width : 1;
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setFormData({ ...formData, img: canvas.toDataURL('image/jpeg', 0.52), rawFile: null, isVideo: false });
            showToast("照片加载成功！");
          };
          img.onerror = () => showToast("无法识别该图片，请换一张");
        };
        reader.readAsDataURL(file);
      }
      e.target.value = '';
    };

    const handleAIPolish = async () => {
      if (!formData.content.trim()) return showToast('请先输入文字内容！');
      setIsPolishing(true);
      try {
        const res = await fetch(buildYuguiApiUrl('/ai/polish'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: formData.content.trim(),
            contentType: formData.type,
            title: formData.title || '',
            name: formData.name || '',
            petType: formData.petType || ''
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) {
          showToast(data.message || '润色服务暂不可用（请确认服务器已配置 /api/ai/polish）');
          return;
        }
        const polished = (data.polished || data.text || data.data?.text || '').trim();
        if (!polished) {
          showToast('润色结果为空，请稍后重试');
          return;
        }
        setPolishDraft(polished);
        setShowPolishModal(true);
      } catch (_) {
        showToast('网络错误，润色失败');
      } finally {
        setIsPolishing(false);
      }
    };

    const toggleTag = (tag) => {
      const currentTags = formData.tags || [];
      if (currentTags.includes(tag)) setFormData({ ...formData, tags: currentTags.filter(t => t !== tag) });
      else if (currentTags.length < 5) setFormData({ ...formData, tags: [...currentTags, tag] });
    };

    const handleConfirmPublish = async () => {
      if (isPublishing) return;
      if (!formData.img) return showToast("发布失败：请先上传照片或视频！");
      if (formData.type === 'post' && !formData.title.trim()) return showToast("发布失败：标题不能为空！");
      if (formData.type === 'adopt' && !formData.name.trim()) return showToast("发布失败：请填写宠物的名称！");
      if (formData.type === 'adopt' && !adoptConventionAgreed) return showToast("发布领养信息前请确认遵守平台公约（非买卖/非变相交易）");
      if (!formData.content.trim()) return showToast("发布失败：正文内容不能为空！");

      setIsPublishing(true);
      setPublishStatus(
        formData.isVideo || formData.rawFile
          ? '正在编码并上传视频，请稍候…'
          : '正在发布…'
      );
      try {
        const ok = await onPublish(formData, formData.type, { draftId: activeDraftId });
        if (!ok) setPublishStatus('');
      } catch (_) {
        showToast('发布异常，请稍后重试', 'error');
        setPublishStatus('');
      } finally {
        setIsPublishing(false);
      }
    };

    return (
      <>
      <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/90 backdrop-blur-md p-0 md:p-4 animate-fade-in">
        <div className="bg-zinc-900 md:rounded-[2rem] w-full h-full md:h-auto md:max-h-[90vh] max-w-5xl flex flex-col shadow-2xl relative overflow-hidden">
          <header className="px-4 md:px-8 py-4 md:py-6 border-b border-white/5 flex justify-between items-center bg-zinc-900 sticky top-0 z-10 md:rounded-t-[2rem]">
            <h2 className="text-xl font-black text-amber-500">创作新内容</h2>
            <button onClick={onClose} className="p-2 bg-white/5 rounded-full hover:bg-white/10 transition-colors"><X size={24}/></button>
          </header>

          <div className="px-4 md:px-8 py-2.5 bg-amber-500/10 border-b border-amber-500/15 shrink-0">
            <p className="text-[11px] md:text-xs text-amber-400/95 font-bold flex items-center flex-wrap gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1"><Wand2 size={13} /> 支持 AI 润色正文</span>
              <span className="text-white/20 hidden sm:inline">·</span>
              <span className="inline-flex items-center gap-1"><Lock size={13} /> 可设「仅自己可见」</span>
            </p>
          </div>

          {showFirstPublishTip && (
            <div className="mx-4 md:mx-8 mt-3 p-3.5 rounded-xl bg-blue-500/10 border border-blue-500/25 flex items-start gap-3 shrink-0">
              <p className="text-xs text-white/80 leading-relaxed flex-1">
                首次发布提示：正文旁有 <strong className="text-amber-400">AI 润色</strong>；左侧可切换 <strong className="text-amber-400">公开 / 仅自己可见</strong>。
              </p>
              <button
                type="button"
                onClick={dismissFirstPublishTip}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-300 text-xs font-black hover:bg-blue-500/30"
              >
                知道了
              </button>
            </div>
          )}

          {activeDraftId && (
            <div className="mx-4 md:mx-8 mt-3 p-3.5 rounded-xl bg-white/5 border border-white/10 flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-white/85">正在编辑草稿：{activeDraftName || '未命名'}</p>
                <p className="text-[10px] text-white/40 mt-0.5">保存会更新草稿箱；发布成功后会自动移出草稿箱</p>
              </div>
              <button
                type="button"
                onClick={handleDeleteCurrentDraft}
                className="shrink-0 w-full sm:w-auto px-4 py-2.5 rounded-xl bg-red-500/10 text-red-400 text-xs font-black border border-red-500/25 hover:bg-red-500/20 transition-colors"
              >
                删除此草稿
              </button>
            </div>
          )}
          
          <div className="flex-1 p-4 md:p-8 flex gap-8 overflow-y-auto no-scrollbar flex-col md:flex-row">
            <div className="w-full md:w-1/2 flex flex-col space-y-6">
               <div className="flex space-x-4 mb-2">
                 <button onClick={() => {
                   revokePreviewBlob(formData.img);
                   if (mediaBlobRef.current) { try { URL.revokeObjectURL(mediaBlobRef.current); } catch (_) {} mediaBlobRef.current = null; }
                   setFormData({ ...defaultFormData, type: 'post' });
                   setAdoptConventionAgreed(false);
                 }} className={`flex-1 py-3 rounded-xl font-black text-sm transition-all ${formData.type==='post' ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/20' : 'bg-white/5 text-white/40 hover:text-white'}`}>分享日常</button>
                 <button onClick={() => {
                   revokePreviewBlob(formData.img);
                   if (mediaBlobRef.current) { try { URL.revokeObjectURL(mediaBlobRef.current); } catch (_) {} mediaBlobRef.current = null; }
                   setFormData({ ...defaultFormData, type: 'adopt' });
                   setAdoptConventionAgreed(false);
                 }} className={`flex-1 py-3 rounded-xl font-black text-sm transition-all ${formData.type==='adopt' ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/20' : 'bg-white/5 text-white/40 hover:text-white'}`}>发布寻主</button>
               </div>
               
               <label className="flex-shrink-0 h-[300px] border-2 border-dashed border-white/10 rounded-[2rem] flex flex-col items-center justify-center text-white/20 hover:text-amber-500 hover:border-amber-500/50 cursor-pointer transition-all overflow-hidden relative bg-black/20 group">
                  <input type="file" accept="image/*,video/*" className="hidden" onChange={handleMediaUpload} />
                  {formData.img ? (
                     <div className="w-full h-full relative">
                       {formData.isVideo ? <video src={formData.img} className="w-full h-full object-cover opacity-90" autoPlay loop muted playsInline /> : <img src={formData.img} className="w-full h-full object-cover opacity-90" alt="upload" />}
                      <button onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        revokePreviewBlob(formData.img);
                        if (mediaBlobRef.current) { try { URL.revokeObjectURL(mediaBlobRef.current); } catch (_) {} mediaBlobRef.current = null; }
                        setFormData({ ...formData, img: '', rawFile: null, isVideo: false });
                      }} className="absolute top-4 right-4 p-2 bg-red-500/80 hover:bg-red-500 rounded-full text-white z-20 shadow-lg">
                         <X size={16} />
                       </button>
                     </div>
                  ) : (
                     <><UploadCloud size={48} className="mb-4 group-hover:scale-110 transition-transform text-white/40" /><p className="text-sm font-bold text-white/60">点击从相册选取或拍摄</p><p className="text-[10px] mt-2 opacity-50 tracking-widest">支持图片/视频上传</p></>
                  )}
               </label>
               
               <div className="bg-white/5 p-5 rounded-[2rem] border border-white/5">
                 <p className="text-xs font-black text-amber-500 mb-3 flex items-center"><Hash size={14} className="mr-1"/> 标签推荐</p>
                 <div className="flex flex-wrap gap-2">
                   {smartTags.map(tag => (
                     <button key={tag} onClick={() => toggleTag(tag)} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${(formData.tags||[]).includes(tag) ? 'bg-amber-500 text-black border-amber-500' : 'bg-transparent text-white/40 border-white/10 hover:border-white/30'}`}>#{tag}</button>
                   ))}
                 </div>
               </div>

               <div className={`bg-white/5 p-5 rounded-[2rem] border border-white/5 transition-shadow ${showFirstPublishTip ? 'ring-2 ring-amber-500/40 shadow-lg shadow-amber-500/10' : ''}`}>
                 <p className="text-xs font-black text-amber-500 mb-3 flex items-center"><EyeOff size={14} className="mr-1"/> 作品可见性</p>
                 <div className="flex space-x-3">
                   <button onClick={() => setFormData({...formData, visibility: 'public'})} className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${formData.visibility === 'public' ? 'bg-amber-500 text-black' : 'bg-white/5 text-white/40'}`}>公开</button>
                   <button onClick={() => setFormData({...formData, visibility: 'private'})} className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${formData.visibility === 'private' ? 'bg-amber-500 text-black' : 'bg-white/5 text-white/40'}`}>仅自己可见</button>
                 </div>
               </div>
            </div>
            
            <div className="w-full md:w-1/2 space-y-6 flex flex-col">
               {formData.type === 'post' ? (
                 <>
                   <input type="text" placeholder="吸引人的标题 (必填)*" value={formData.title} onChange={e=>setFormData({...formData, title: e.target.value})} className="w-full bg-transparent border-b border-white/10 py-3 text-xl font-bold outline-none focus:border-amber-500 text-white" />
                   <div className="relative flex-1 flex flex-col">
                      <textarea placeholder="添加正文，分享日常趣事..." value={formData.content} onChange={e=>setFormData({...formData, content: e.target.value})} className="w-full flex-1 min-h-[150px] bg-transparent border-b border-white/10 py-3 text-sm outline-none focus:border-amber-500 resize-none text-white leading-relaxed" />
                      <button onClick={handleAIPolish} disabled={isPolishing} className={`absolute bottom-4 right-2 px-4 py-2 bg-amber-500/10 text-amber-500 font-bold text-xs rounded-full flex items-center hover:bg-amber-500/20 transition-colors ${showFirstPublishTip ? 'ring-2 ring-amber-500/50 animate-pulse' : ''}`}>
                        {isPolishing ? <Loader2 size={14} className="animate-spin mr-1"/> : <Wand2 size={14} className="mr-1"/>} AI 润色文案
                      </button>
                   </div>
                 </>
               ) : (
                 <>
                   <input type="text" placeholder="宠物昵称 (必填)*" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-white/5 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-1 focus:ring-amber-500 text-white" />
                   <div className="grid grid-cols-2 gap-4">
                     <input type="text" placeholder="品种 (如: 橘猫)" value={formData.petType} onChange={e=>setFormData({...formData, petType: e.target.value})} className="w-full bg-white/5 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-1 focus:ring-amber-500 text-white" />
                     <input type="text" placeholder="年龄 (如: 3个月)" value={formData.age} onChange={e=>setFormData({...formData, age: e.target.value})} className="w-full bg-white/5 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-1 focus:ring-amber-500 text-white" />
                   </div>
                   <div className="relative flex-1 flex flex-col">
                      <textarea placeholder="详细描述宠物的性格及领养要求 (必填)*" value={formData.content} onChange={e=>setFormData({...formData, content: e.target.value})} className="w-full flex-1 min-h-[100px] bg-white/5 rounded-xl p-4 text-sm outline-none focus:ring-1 focus:ring-amber-500 resize-none text-white leading-relaxed mb-4 pr-28 pb-12" />
                      <button type="button" onClick={handleAIPolish} disabled={isPolishing} className={`absolute bottom-6 right-3 px-4 py-2 bg-amber-500/10 text-amber-500 font-bold text-xs rounded-full flex items-center hover:bg-amber-500/20 transition-colors ${showFirstPublishTip ? 'ring-2 ring-amber-500/50 animate-pulse' : ''}`}>
                        {isPolishing ? <Loader2 size={14} className="animate-spin mr-1"/> : <Wand2 size={14} className="mr-1"/>} AI 润色文案
                      </button>
                   </div>
                   <div>
                    <p className="text-[10px] text-white/40 font-black uppercase mb-2">健康指标声明</p>
                    <div className="flex flex-wrap gap-2">
                       {['vaccine', 'deworm', 'neuter'].map(key => {
                         const labels = {vaccine: '已接种疫苗', deworm: '已定期驱虫', neuter: '已绝育'};
                         return (
                           <button key={key} onClick={() => setFormData({...formData, health: {...formData.health, [key]: !formData.health[key]}})} className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${formData.health[key] ? 'bg-amber-500 text-black border-amber-500 shadow-lg shadow-amber-500/20' : 'bg-transparent border-white/20 text-white/60 hover:border-white/50'}`}>
                             {labels[key]}
                           </button>
                         )
                       })}
                    </div>
                  </div>
                 </>
               )}
            </div>
          </div>
          {formData.type === 'adopt' && (
            <div className="mx-4 md:mx-8 mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={adoptConventionAgreed}
                  onChange={() => setAdoptConventionAgreed(!adoptConventionAgreed)}
                  className="mt-1 accent-amber-500"
                />
                <span className="text-[11px] text-white/70 leading-relaxed">
                  我确认本次发布为<span className="text-amber-400 font-bold">公益领养/送养</span>，非宠物买卖、有偿转让或变相收费交易，并遵守《予归平台公约》。
                </span>
              </label>
            </div>
          )}
          <footer className="px-4 md:px-8 py-4 md:py-6 border-t border-white/5 bg-zinc-900/50 flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 md:rounded-b-[2rem]">
            {publishStatus && (
              <p className="w-full sm:mr-auto text-xs font-bold text-amber-400/90 flex items-center gap-2 order-first sm:order-none">
                <Loader2 size={14} className="animate-spin shrink-0" />
                <span>{publishStatus}</span>
              </p>
            )}
            <button
              type="button"
              onClick={handleSaveToDraftBox}
              disabled={isPublishing}
              className="w-full sm:w-auto sm:mr-auto px-5 py-3 rounded-full font-bold text-xs text-amber-400 bg-amber-500/10 border border-amber-500/25 hover:bg-amber-500/15 transition-colors disabled:opacity-40"
            >
              保存到草稿箱
            </button>
            <button 
              type="button"
              disabled={isPublishing}
              onClick={handleConfirmPublish}
              className="w-full md:w-auto px-10 py-4 md:py-3 bg-amber-500 text-black font-black rounded-full hover:bg-amber-400 active:scale-95 transition-all shadow-xl shadow-amber-500/20 tracking-widest text-sm disabled:opacity-60 disabled:pointer-events-none flex items-center justify-center gap-2"
            >
              {isPublishing ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  发布中…
                </>
              ) : (
                '确认并发布内容'
              )}
            </button>
          </footer>
        </div>
      </div>

      {showPolishModal && (
        <div className="fixed inset-0 z-[5100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-[2rem] w-full max-w-lg p-6 shadow-2xl relative">
            <button
              type="button"
              aria-label="关闭"
              onClick={() => {
                setShowPolishModal(false);
                setPolishDraft('');
              }}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/5 text-white/50 hover:text-white hover:bg-white/10"
            >
              <X size={18} />
            </button>
            <h3 className="text-lg font-black text-amber-500 mb-2 pr-10">AI 润色结果</h3>
            <p className="text-[10px] text-white/40 mb-3">可编辑后再决定：采用润色后的文字，或保留你原来写的。</p>
            <textarea
              value={polishDraft}
              onChange={(e) => setPolishDraft(e.target.value)}
              className="w-full min-h-[160px] bg-black/40 border border-white/10 rounded-xl p-4 text-sm text-white/90 outline-none focus:border-amber-500/50 resize-y mb-4"
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, content: polishDraft }));
                  setShowPolishModal(false);
                  setPolishDraft('');
                  showToast?.('已采用润色文案');
                }}
                className="flex-1 py-3 rounded-xl font-black bg-amber-500 text-black hover:bg-amber-400"
              >
                采用润色
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPolishModal(false);
                  setPolishDraft('');
                  showToast?.('已保留原文');
                }}
                className="flex-1 py-3 rounded-xl font-bold bg-white/5 border border-white/15 text-white/80 hover:bg-white/10"
              >
                保留原文
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
};

export const ReportModal = ({ targetInfo, onSubmit, onClose }) => {
    const [selectedReason, setSelectedReason] = useState('');
    const [details, setDetails] = useState("");
    return (
      <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
        <div className="bg-zinc-900 border border-white/10 rounded-[2rem] w-full max-w-md p-8 shadow-2xl relative">
          <button onClick={onClose} className="absolute top-6 right-6 p-2 text-white/40 hover:text-white bg-white/5 rounded-full transition-colors hover:bg-white/10"><X size={24}/></button>
          <h2 className="text-2xl font-black mb-2 text-red-500 flex items-center"><Flag size={24} className="mr-2"/> 违规举报</h2>
          <p className="text-xs text-white/40 mb-6 uppercase tracking-widest">正在举报: {targetInfo?.type === 'adopt' ? '领养信息' : '用户作品'}</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
             {reportReasons.map(c => (
               <button key={c} onClick={() => setSelectedReason(c)} className={`px-4 py-3 rounded-xl font-bold text-xs transition-all border ${selectedReason === c ? 'bg-red-500 text-white border-red-500' : 'bg-white/5 text-white/60 hover:border-red-500 hover:text-red-500'}`}>{c}</button>
             ))}
          </div>
          <textarea placeholder="详细描述（选填）..." value={details} onChange={e=>setDetails(e.target.value)} className="w-full h-32 bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-white/80 outline-none focus:border-red-500 mb-6 resize-none" />
          <button disabled={!selectedReason} onClick={() => onSubmit(selectedReason, details || "无详细描述")} className="w-full py-4 bg-red-600 text-white font-black rounded-xl hover:bg-red-500 transition-colors shadow-lg shadow-red-600/20 disabled:opacity-30">提交云端审核</button>
        </div>
      </div>
    );
};

export const FeedbackModal = ({ onSubmit, onClose }) => {
  const [text, setText] = useState('');
  const [type, setType] = useState('建议');
  return (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-zinc-900 border border-white/10 rounded-[2rem] w-full max-w-md p-8 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-6 right-6 p-2 text-white/40 hover:text-white bg-white/5 rounded-full transition-colors hover:bg-white/10"><X size={24}/></button>
        <h2 className="text-2xl font-black mb-6 text-amber-500 flex items-center"><MessageSquare size={24} className="mr-2"/> 官方反馈</h2>
        <div className="flex space-x-4 mb-6">
           {['建议', 'BUG', '投诉'].map(t => (
             <button key={t} onClick={() => setType(t)} className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all border ${type === t ? 'bg-amber-500 text-black border-amber-500 shadow-lg shadow-amber-500/20' : 'bg-white/5 text-white/40 border-white/10'}`}>{t}</button>
           ))}
        </div>
        <textarea placeholder={`请详细描述您的${type}...`} value={text} onChange={e=>setText(e.target.value)} className="w-full h-40 bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-white/80 outline-none focus:border-amber-500 mb-6 resize-none" />
        <button disabled={!text.trim()} onClick={() => onSubmit(text, type)} className="w-full py-4 bg-amber-500 text-black font-black rounded-xl disabled:opacity-30 hover:bg-amber-400 transition-colors shadow-lg shadow-amber-500/20 tracking-widest">提交至官方</button>
      </div>
    </div>
  );
};

/** 从举报记录解析被举报内容（优先实时数据，其次提交时的快照） */
const resolveReportedContent = (report, posts, pets) => {
  const targetId = String(report.targetId || report.contentId || '');
  if (!targetId) return null;

  const targetType = report.targetType || report.type || 'post';
  const live =
    [...(Array.isArray(posts) ? posts : []), ...(Array.isArray(pets) ? pets : [])].find(
      (p) => String(p.id) === targetId
    ) || null;
  const preview = report.contentPreview;

  if (live) {
    return {
      id: live.id,
      type: live.type === 'adopt' || targetType === 'adopt' ? 'adopt' : 'post',
      title: live.title || live.name || preview?.title || '无标题',
      text: (live.content || live.description || preview?.text || '').slice(0, 400),
      user: live.user || preview?.user || '未知用户',
      userId: live.userId || preview?.userId || '',
      img: live.img || preview?.img || '',
      isVideo: isPostVideoMedia(live),
      fromSnapshot: false
    };
  }

  if (preview) {
    return {
      id: targetId,
      type: preview.type || targetType,
      title: preview.title || '无标题',
      text: (preview.text || '').slice(0, 400),
      user: preview.user || '未知用户',
      userId: preview.userId || '',
      img: preview.img || '',
      isVideo: !!preview.isVideo,
      fromSnapshot: true
    };
  }

  return {
    id: targetId,
    type: targetType,
    title: '内容已下架或不存在',
    text: '',
    user: '—',
    userId: '',
    img: '',
    isVideo: false,
    missing: true
  };
};

const ReportedContentPreview = ({ report, posts, pets, onView }) => {
  const content = resolveReportedContent(report, posts, pets);
  if (!content) return null;

  const typeLabel = content.type === 'adopt' ? '领养信息' : '日常作品';

  return (
    <div className="bg-black/40 border border-white/10 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-black text-white/40 uppercase tracking-widest">被举报内容</p>
        <span className="text-[10px] font-bold bg-white/10 text-white/60 px-2 py-0.5 rounded-full">{typeLabel}</span>
      </div>
      <div className="flex gap-3 md:gap-4">
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-white/5 shrink-0 relative border border-white/10">
          {content.img ? (
            content.isVideo ? (
              <>
                <video src={content.img} className="w-full h-full object-cover" muted playsInline />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <PlaySquare size={22} className="text-white/90" />
                </div>
              </>
            ) : (
              <img src={content.img} alt="" className="w-full h-full object-cover" />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/20 text-[10px] font-bold">无封面</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-sm md:text-base text-white truncate">{content.title}</p>
          <p className="text-xs text-amber-500/80 mt-0.5">发布者：@{content.user}</p>
          {content.text ? (
            <p className="text-xs text-white/60 mt-2 line-clamp-3 leading-relaxed">{content.text}</p>
          ) : content.missing ? (
            <p className="text-xs text-white/40 mt-2">ID: {content.id}（可能已被删除）</p>
          ) : null}
          {content.fromSnapshot && (
            <p className="text-[10px] text-white/30 mt-1">以下为举报提交时的快照</p>
          )}
        </div>
      </div>
      {!content.missing && onView && (
        <button
          type="button"
          onClick={() => onView(content)}
          className="mt-3 w-full md:w-auto min-h-[44px] md:min-h-0 px-4 py-3 md:py-2 text-xs font-bold rounded-xl bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 transition-colors"
        >
          查看原文 →
        </button>
      )}
    </div>
  );
};

const BAN_DURATION_OPTIONS = [
  { label: '1 天', days: 1 },
  { label: '7 天', days: 7 },
  { label: '30 天', days: 30 },
  { label: '永久', days: -1 }
];

const ADMIN_GRANT_OPTIONS = [
  { label: '7 天', days: 7 },
  { label: '30 天', days: 30 },
  { label: '90 天', days: 90 },
  { label: '永久', days: -1 }
];

const formatBanStatus = (user) => {
  if (!user?.isBanned) return null;
  let status = '';
  if (user.banDays === -1) status = '永久封禁中';
  else if (user.banUntil) {
    status = `封禁至 ${new Date(user.banUntil).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
  } else if (user.banDays > 0) status = `封禁 ${user.banDays} 天`;
  else status = '已封禁';
  if (user.bannedByName || user.bannedBy) {
    status += ` · 操作者：${user.bannedByName || user.bannedBy}`;
  }
  return status;
};

const formatAdminRoleStatus = (user) => {
  if (user?.role !== 'admin') return null;
  if (user.adminUntil && Number(user.adminUntil) > Date.now()) {
    return `临时管理员 · 至 ${new Date(user.adminUntil).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
  }
  if (user.adminDays === -1 || (!user.adminUntil && user.role === 'admin')) {
    return user.adminGrantedByName ? `永久管理员 · 授予者：${user.adminGrantedByName}` : '永久管理员';
  }
  if (user.adminDays > 0) return `临时管理员 ${user.adminDays} 天`;
  return null;
};

const ADMIN_ACTION_BTN =
  'min-h-[44px] md:min-h-0 px-4 py-3 md:py-2 font-bold text-xs rounded-xl transition-colors active:scale-[0.98]';
const ADMIN_FILTER_PILL =
  'min-h-[40px] md:min-h-0 px-4 md:px-3 py-2.5 md:py-1.5 rounded-full text-xs font-bold border transition-colors shrink-0';

const BanDurationPanel = ({ targetLabel, selectedDays, onSelectDays, onConfirm, onCancel, requestMode = false }) => (
  <div className="fixed inset-x-0 bottom-0 z-[4100] p-4 md:p-6 bg-zinc-900/98 border-t border-amber-500/30 backdrop-blur-xl safe-area-bottom shadow-2xl">
    <p className="text-sm font-black text-white mb-1">{requestMode ? '申请封禁（需超级管理员审批）' : '选择封禁时长'}</p>
    <p className="text-xs text-white/45 mb-4 truncate">对象：{targetLabel || '该用户'}</p>
    <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 mb-4">
      {BAN_DURATION_OPTIONS.map((opt) => (
        <button
          key={opt.days}
          type="button"
          onClick={() => onSelectDays(opt.days)}
          className={`min-h-[44px] md:min-h-0 px-4 py-2.5 rounded-xl text-xs font-black border transition-colors ${
            selectedDays === opt.days
              ? 'bg-red-500 text-white border-red-500'
              : 'bg-white/5 text-white/60 border-white/10 hover:border-white/25'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
    <div className="flex gap-2">
      <button type="button" onClick={onCancel} className="flex-1 min-h-[48px] py-3 rounded-xl bg-white/5 text-white/60 text-sm font-bold">
        取消
      </button>
      <button type="button" onClick={onConfirm} className="flex-1 min-h-[48px] py-3 rounded-xl bg-red-600 text-white text-sm font-black">
        {requestMode ? '提交申请' : '确认封禁'}
      </button>
    </div>
  </div>
);

const AdminGrantPanel = ({ targetLabel, selectedDays, onSelectDays, onConfirm, onCancel }) => (
  <div className="fixed inset-x-0 bottom-0 z-[4100] p-4 md:p-6 bg-zinc-900/98 border-t border-blue-500/30 backdrop-blur-xl safe-area-bottom shadow-2xl">
    <p className="text-sm font-black text-white mb-1">授予管理员权限</p>
    <p className="text-xs text-white/45 mb-4 truncate">对象：{targetLabel || '该用户'}</p>
    <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 mb-4">
      {ADMIN_GRANT_OPTIONS.map((opt) => (
        <button
          key={opt.days}
          type="button"
          onClick={() => onSelectDays(opt.days)}
          className={`min-h-[44px] md:min-h-0 px-4 py-2.5 rounded-xl text-xs font-black border transition-colors ${
            selectedDays === opt.days
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-white/5 text-white/60 border-white/10 hover:border-white/25'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
    <div className="flex gap-2">
      <button type="button" onClick={onCancel} className="flex-1 min-h-[48px] py-3 rounded-xl bg-white/5 text-white/60 text-sm font-bold">
        取消
      </button>
      <button type="button" onClick={onConfirm} className="flex-1 min-h-[48px] py-3 rounded-xl bg-blue-500 text-white text-sm font-black">
        确认授予
      </button>
    </div>
  </div>
);

const getUserMatchIds = (user) => {
  if (!user) return [];
  return [
    ...new Set(
      [user.id, user.uid, user.phone, user.userId, user._id].filter(Boolean).map(String)
    )
  ];
};

const itemBelongsToUser = (item, matchIds) => {
  if (!item || !matchIds.length) return false;
  const owners = [item.userId, item.uid, item.phone].filter(Boolean).map(String);
  return owners.some((id) => matchIds.includes(id));
};

const AdminUserDetailPanel = ({
  user,
  contentPool,
  reports,
  feedbackList,
  isSuperAdmin,
  onBack,
  onAction,
  openBanPicker,
  openAdminGrantPicker,
  onViewReportedContent
}) => {
  const matchIds = getUserMatchIds(user);
  const userContent = contentPool.filter((item) => itemBelongsToUser(item, matchIds));
  const activeContent = userContent.filter((item) => !isContentDeleted(item));
  const removedContent = userContent.filter((item) => isContentDeleted(item));
  const userContentIds = new Set(userContent.map((p) => String(p.id)));
  const reportsAgainst = reports.filter((r) => userContentIds.has(String(r.targetId)));
  const pendingReportsAgainst = reportsAgainst.filter((r) => !r.status || r.status === 'pending');
  const reportsByUser = reports.filter((r) =>
    matchIds.some(
      (id) =>
        String(r.reporterId) === id ||
        String(r.reporterUid) === id ||
        String(r.reporterPhone) === id
    )
  );
  const userFeedback = feedbackList.filter((f) =>
    matchIds.some(
      (id) => String(f.uid) === id || String(f.userId) === id || String(f.phone) === id
    )
  );
  const userId = user.userId || user.id || user._id;

  return (
    <div className="absolute inset-0 z-20 bg-stone-950 flex flex-col animate-fade-in safe-area-top">
      <header className="px-4 md:px-8 py-3 md:py-4 border-b border-white/5 flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="p-2.5 md:p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors yugui-admin-touch"
          aria-label="返回用户列表"
        >
          <ChevronLeft size={22} />
        </button>
        <h3 className="text-base md:text-lg font-black text-amber-500 truncate">用户详情</h3>
      </header>

      <div className="flex-1 overflow-y-auto no-scrollbar p-4 md:p-8 pb-8 safe-area-bottom space-y-5 md:space-y-6">
        <div className="flex items-start gap-4">
          <img
            src={user.avatar || DEFAULT_AVATAR}
            alt=""
            className="w-16 h-16 rounded-full border-2 border-amber-500/30 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-xl font-black flex items-center flex-wrap gap-2">
              {user.name || '未命名用户'}
              {user.role === 'super_admin' && (
                <span className="text-[10px] font-black bg-amber-500 text-black px-2 py-0.5 rounded-full">超级管理员</span>
              )}
              {user.role === 'admin' && (
                <span className="text-[10px] font-black bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">管理员</span>
              )}
              {user.isBanned && (
                <span className="text-[10px] font-black bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">已封禁</span>
              )}
            </p>
            <p className="text-xs text-white/45 mt-1">UID：{user.uid || '—'}</p>
            <p className="text-xs text-white/45">手机号：{user.phone || '—'}</p>
            {user.isBanned && formatBanStatus(user) && (
              <p className="text-xs text-red-400/90 mt-1">{formatBanStatus(user)}</p>
            )}
            {user.role === 'admin' && formatAdminRoleStatus(user) && (
              <p className="text-xs text-blue-400/90 mt-1">{formatAdminRoleStatus(user)}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {[
            { label: '正常内容', value: activeContent.length, accent: 'text-emerald-400' },
            { label: '已下架', value: removedContent.length, accent: 'text-red-400' },
            { label: '被举报', value: reportsAgainst.length, accent: 'text-orange-400' },
            { label: '待处理举报', value: pendingReportsAgainst.length, accent: 'text-amber-400' }
          ].map((stat) => (
            <div key={stat.label} className="bg-zinc-900 border border-white/5 rounded-xl p-3 text-center">
              <p className={`text-xl font-black ${stat.accent}`}>{stat.value}</p>
              <p className="text-[10px] text-white/45 font-bold mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {user.role !== 'super_admin' && String(user.phone || '') !== OFFICIAL_ADMIN_PHONE && (
          <div className="flex flex-col md:flex-row md:flex-wrap gap-2">
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() =>
                  user.role === 'admin'
                    ? onAction('removeAdmin', { ...user, userId })
                    : openAdminGrantPicker({ ...user, userId })
                }
                className={`w-full md:w-auto ${ADMIN_ACTION_BTN} ${
                  user.role === 'admin' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-500 text-white'
                }`}
              >
                {user.role === 'admin' ? '取消管理员' : '设为管理员'}
              </button>
            )}
            {isSuperAdmin ? (
              <button
                type="button"
                onClick={() =>
                  user.isBanned
                    ? onAction('unbanUser', { ...user, userId })
                    : openBanPicker({ ...user, userId }, user.name || user.phone)
                }
                className={`w-full md:w-auto ${ADMIN_ACTION_BTN} ${
                  user.isBanned ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                }`}
              >
                {user.isBanned ? '解封账号' : '封禁账号'}
              </button>
            ) : !user.isBanned ? (
              <button
                type="button"
                onClick={() => openBanPicker({ ...user, userId }, user.name || user.phone)}
                className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-orange-500 text-white`}
              >
                申请封禁
              </button>
            ) : (
              <p className="text-xs text-white/40 px-1">该用户已封禁，解封需超级管理员操作</p>
            )}
          </div>
        )}

        <section>
          <h4 className="text-sm font-black text-white/60 mb-3">Ta 发布的内容 ({userContent.length})</h4>
          {userContent.length === 0 ? (
            <p className="text-xs text-white/30 py-6 text-center">暂无发布记录</p>
          ) : (
            <div className="space-y-2">
              {userContent.slice(0, 20).map((item) => {
                const removed = isContentDeleted(item);
                return (
                  <div
                    key={`${item.type}_${item.id}`}
                    className="bg-zinc-900 border border-white/5 rounded-xl p-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate flex items-center gap-2">
                        {item.title || item.name || '无标题'}
                        {removed && (
                          <span className="text-[9px] text-red-400 bg-red-500/15 px-1.5 py-0.5 rounded-full shrink-0">
                            已下架
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-white/40">
                        {item.type === 'adopt' ? '领养信息' : '日常作品'}
                        {item.createdAt
                          ? ` · ${new Date(item.createdAt).toLocaleDateString()}`
                          : ''}
                      </p>
                    </div>
                    {!removed && onViewReportedContent && (
                      <button
                        type="button"
                        onClick={() =>
                          onViewReportedContent({
                            id: item.id,
                            type: item.type === 'adopt' ? 'adopt' : 'post'
                          })
                        }
                        className="text-[10px] font-bold text-amber-500 shrink-0"
                      >
                        查看 →
                      </button>
                    )}
                  </div>
                );
              })}
              {userContent.length > 20 && (
                <p className="text-[10px] text-white/30 text-center">仅显示最近 20 条</p>
              )}
            </div>
          )}
        </section>

        <section>
          <h4 className="text-sm font-black text-white/60 mb-3">
            针对 Ta 的举报 ({reportsAgainst.length})
          </h4>
          {reportsAgainst.length === 0 ? (
            <p className="text-xs text-white/30 py-4 text-center">暂无相关举报</p>
          ) : (
            <div className="space-y-2">
              {reportsAgainst.slice(0, 10).map((r) => (
                <div key={r.id} className="bg-zinc-900 border border-white/5 rounded-xl p-3">
                  <p className="text-xs font-bold text-red-400">{r.reason}</p>
                  <p className="text-[10px] text-white/40 mt-1">
                    {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
                    {' · '}
                    {r.status === 'handled' ? '已处理' : '待处理'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        {(reportsByUser.length > 0 || userFeedback.length > 0) && (
          <section className="grid md:grid-cols-2 gap-4">
            {reportsByUser.length > 0 && (
              <div>
                <h4 className="text-sm font-black text-white/60 mb-3">Ta 发起的举报 ({reportsByUser.length})</h4>
                <div className="space-y-2">
                  {reportsByUser.slice(0, 5).map((r) => (
                    <div key={r.id} className="bg-zinc-900 border border-white/5 rounded-xl p-3">
                      <p className="text-xs text-white/70">{r.reason}</p>
                      <p className="text-[10px] text-white/35 mt-1">
                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {userFeedback.length > 0 && (
              <div>
                <h4 className="text-sm font-black text-white/60 mb-3">Ta 的反馈 ({userFeedback.length})</h4>
                <div className="space-y-2">
                  {userFeedback.slice(0, 5).map((f) => (
                    <div key={f.id} className="bg-zinc-900 border border-white/5 rounded-xl p-3">
                      <p className="text-xs font-bold text-amber-500/80">{f.type}</p>
                      <p className="text-xs text-white/70 mt-1 line-clamp-2">{f.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

/** 维护期间向普通用户展示的全屏说明 */
export const SiteMaintenanceScreen = ({
  config,
  isLoggedIn,
  onAdminLogin,
  onLogout
}) => {
  const notice = buildMaintenanceNotice(config || {});
  const windowText = formatMaintenanceWindow(config || {});
  const active = isMaintenanceActive(config || {});

  return (
    <div
      className="min-h-screen bg-stone-950 text-white flex flex-col items-center justify-center p-6 relative overflow-hidden"
      style={{ minHeight: '100vh' }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(245,158,11,0.08)_0%,_transparent_55%)] pointer-events-none" />
      <div className="max-w-lg w-full relative z-10 text-center space-y-6">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-amber-500/10 border border-amber-500/30 mb-2">
          <Construction size={40} className="text-amber-500" />
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.35em] text-amber-500/70 mb-3">予归平台 · 官方公告</p>
          <h1 className="text-2xl md:text-3xl font-black text-white mb-2">{notice.title}</h1>
          {active && (
            <p className="text-sm text-amber-400/90 font-bold">维护时间：{windowText}</p>
          )}
        </div>
        <div className="bg-zinc-900/80 border border-white/10 rounded-2xl p-6 text-left">
          <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">{notice.body}</p>
        </div>
        <p className="text-xs text-white/35">维护期间普通用户暂无法访问。平台管理员请使用下方入口重新登录验证。</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          {!isLoggedIn && (
            <button
              type="button"
              onClick={onAdminLogin}
              className="px-6 py-3 rounded-xl bg-amber-500 text-black text-sm font-black hover:bg-amber-400 transition-colors"
            >
              管理员登录验证
            </button>
          )}
          {isLoggedIn && (
            <button
              type="button"
              onClick={onLogout}
              className="px-6 py-3 rounded-xl bg-white/5 border border-white/15 text-white/60 text-sm font-bold hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
            >
              <LogOut size={16} /> 退出登录
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ADMIN_TAB_ICONS = {
  数据概览: BarChart3,
  举报审核: Flag,
  用户反馈: MessageSquare,
  用户管理: Users,
  管理员监管: ShieldPlus,
  公告发布: Megaphone,
  网站维护: Construction,
  内容管理: FileText,
  操作日志: LayoutDashboard
};

export const AdminModal = ({
  userProfile,
  reports,
  feedbackList,
  usersList,
  posts,
  pets,
  adminAllContent = [],
  announcements = [],
  auditLogs = [],
  banRequests = [],
  siteMaintenance = {},
  onClose,
  onAction,
  onViewReportedContent,
  showToast
}) => {
  const isSuperAdmin = isSuperAdminProfile(userProfile);
  const adminTabs = isSuperAdmin
    ? ['数据概览', '举报审核', '用户反馈', '用户管理', '管理员监管', '公告发布', '网站维护', '内容管理', '操作日志']
    : ['数据概览', '举报审核', '用户反馈', '用户管理', '内容管理', '操作日志'];
  const adminTabShortLabels = {
    数据概览: '概览',
    举报审核: '举报',
    用户反馈: '反馈',
    用户管理: '用户',
    管理员监管: '监管',
    公告发布: '公告',
    网站维护: '维护',
    内容管理: '内容',
    操作日志: '日志'
  };
  const pendingBanRequests = (banRequests || []).filter((r) => r.status === 'pending');
  const [adminTab, setAdminTab] = useState('数据概览');
  const [newAnnTitle, setNewAnnTitle] = useState('');
  const [newAnnContent, setNewAnnContent] = useState('');
  const [showAnnPreview, setShowAnnPreview] = useState(false);
  const [searchUid, setSearchUid] = useState('');
  const [banPick, setBanPick] = useState(null);
  const [adminGrantPick, setAdminGrantPick] = useState(null);
  const [modBanPick, setModBanPick] = useState(null);
  const [feedbackReplyDraft, setFeedbackReplyDraft] = useState({});
  const [contentTypeFilter, setContentTypeFilter] = useState('all');
  const [contentStatusFilter, setContentStatusFilter] = useState('all');
  const [contentSearch, setContentSearch] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState('all');
  const [auditTimeFilter, setAuditTimeFilter] = useState('all');
  const [auditActorSearch, setAuditActorSearch] = useState('');
  const [detailUser, setDetailUser] = useState(null);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [maintEnabled, setMaintEnabled] = useState(false);
  const [maintTitle, setMaintTitle] = useState('网站维护中');
  const [maintMessage, setMaintMessage] = useState('');
  const [maintStart, setMaintStart] = useState('');
  const [maintEnd, setMaintEnd] = useState('');
  const [maintSaving, setMaintSaving] = useState(false);

  useEffect(() => {
    const cfg = siteMaintenance || {};
    setMaintEnabled(!!cfg.enabled);
    setMaintTitle(cfg.title || '网站维护中');
    setMaintMessage(cfg.message || '');
    setMaintStart(toDatetimeLocalValue(cfg.startAt));
    setMaintEnd(toDatetimeLocalValue(cfg.endAt));
  }, [siteMaintenance]);

  const handleSaveMaintenance = async () => {
    const startAt = fromDatetimeLocalValue(maintStart);
    const endAt = fromDatetimeLocalValue(maintEnd);
    if (maintEnabled && startAt && endAt && endAt <= startAt) {
      showToast?.('结束时间须晚于开始时间');
      return;
    }
    setMaintSaving(true);
    try {
      const ok = await onAction('updateMaintenance', {
        enabled: maintEnabled,
        title: maintTitle.trim() || '网站维护中',
        message: maintMessage.trim(),
        startAt,
        endAt
      });
      if (ok) {
        showToast?.(maintEnabled ? '已开启网站维护模式' : '已关闭网站维护模式');
      }
    } finally {
      setMaintSaving(false);
    }
  };

  const handleQuickToggleMaintenance = async () => {
    const next = !maintEnabled;
    if (next && !window.confirm('确定暂停网站对普通用户的访问？管理员仍可正常使用。')) return;
    setMaintEnabled(next);
    setMaintSaving(true);
    try {
      const ok = await onAction('updateMaintenance', {
        enabled: next,
        immediate: next,
        title: maintTitle.trim() || '网站维护中',
        message: maintMessage.trim(),
        startAt: next ? null : fromDatetimeLocalValue(maintStart),
        endAt: fromDatetimeLocalValue(maintEnd)
      });
      if (ok) {
        showToast?.(next ? '维护模式已开启' : '维护模式已关闭，网站已恢复');
      } else {
        setMaintEnabled(!next);
      }
    } finally {
      setMaintSaving(false);
    }
  };

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const todayTs = startOfToday.getTime();

  const pendingReports = reports.filter(
    (r) => !r.status || r.status === 'pending'
  );

  useEffect(() => {
    if (adminTab !== '举报审核') {
      setSelectedReportIds([]);
      return;
    }
    setSelectedReportIds((prev) =>
      prev.filter((id) =>
        reports.some(
          (r) => String(r.id) === id && (!r.status || r.status === 'pending')
        )
      )
    );
  }, [adminTab, reports]);

  const toggleReportSelect = (reportId) => {
    const sid = String(reportId);
    setSelectedReportIds((prev) =>
      prev.includes(sid) ? prev.filter((id) => id !== sid) : [...prev, sid]
    );
  };

  const allReportsSelected =
    pendingReports.length > 0 &&
    pendingReports.every((r) => selectedReportIds.includes(String(r.id)));

  const toggleSelectAllReports = () => {
    if (allReportsSelected) {
      setSelectedReportIds([]);
    } else {
      setSelectedReportIds(pendingReports.map((r) => String(r.id)).filter(Boolean));
    }
  };

  const handleBatchIgnoreReports = () => {
    if (!selectedReportIds.length) {
      showToast?.('请先选择举报');
      return;
    }
    if (!window.confirm(`确定忽略已选 ${selectedReportIds.length} 条举报？`)) return;
    onAction('batchIgnoreReports', { reportIds: [...selectedReportIds] });
    setSelectedReportIds([]);
  };

  const handleBatchTakedownReports = () => {
    const selected = pendingReports.filter((r) => selectedReportIds.includes(String(r.id)));
    const contentMap = new Map();
    selected.forEach((report) => {
      const contentId = report.targetId;
      if (!contentId) return;
      const contentType = report.targetType || report.type || 'post';
      contentMap.set(`${contentType}:${contentId}`, { contentId, contentType });
    });
    const items = Array.from(contentMap.values());
    if (!items.length) {
      showToast?.('所选举报没有关联的可下架内容');
      return;
    }
    if (
      !window.confirm(
        `确定批量下架 ${items.length} 条内容？已选 ${selectedReportIds.length} 条举报将一并办结。`
      )
    ) {
      return;
    }
    onAction('batchTakedownReports', { items, reportIds: [...selectedReportIds] });
    setSelectedReportIds([]);
  };

  const pendingFeedback = feedbackList.filter(
    (f) => !f.status || f.status === 'pending'
  );
  const allContent = [...posts, ...pets];
  const todayContentCount = allContent.filter((item) => {
    const ts = Number(item.createdAt || item.createTime || 0);
    return Number.isFinite(ts) && ts >= todayTs;
  }).length;
  const bannedUserCount = usersList.filter((u) => u.isBanned).length;

  const dashboardStats = [
    {
      label: '待处理举报',
      value: pendingReports.length,
      icon: Flag,
      accent: 'text-red-400',
      bg: 'bg-red-500/10 border-red-500/20',
      tab: '举报审核'
    },
    {
      label: '待处理反馈',
      value: pendingFeedback.length,
      icon: MessageSquare,
      accent: 'text-amber-400',
      bg: 'bg-amber-500/10 border-amber-500/20',
      tab: '用户反馈'
    },
    {
      label: '注册用户',
      value: usersList.length,
      icon: Users,
      accent: 'text-blue-400',
      bg: 'bg-blue-500/10 border-blue-500/20',
      tab: '用户管理'
    },
    {
      label: '今日新发',
      value: todayContentCount,
      icon: CalendarDays,
      accent: 'text-emerald-400',
      bg: 'bg-emerald-500/10 border-emerald-500/20',
      tab: '内容管理'
    },
    {
      label: '全部内容',
      value: allContent.length,
      icon: FileText,
      accent: 'text-white/70',
      bg: 'bg-white/5 border-white/10',
      tab: '内容管理'
    },
    {
      label: '封禁账号',
      value: bannedUserCount,
      icon: Ban,
      accent: 'text-orange-400',
      bg: 'bg-orange-500/10 border-orange-500/20',
      tab: '用户管理'
    }
  ];

  const q = (searchUid || '').trim();
  const filteredUsers = q
    ? usersList.filter((u) => {
        const hit =
          String(u.uid || '').includes(q) ||
          String(u.phone || '').includes(q) ||
          String(u.id || '').includes(q) ||
          String(u._id || '').includes(q);
        return hit;
      })
    : usersList;

  const openBanPicker = (payload, label) => {
    setBanPick({ payload, label: label || '该用户', selectedDays: 7 });
  };

  const confirmBan = () => {
    if (!banPick) return;
    onAction(isSuperAdmin ? 'banUser' : 'requestBanUser', {
      ...banPick.payload,
      days: banPick.selectedDays,
      label: banPick.label
    });
    setBanPick(null);
  };

  const openAdminGrantPicker = (payload) => {
    setAdminGrantPick({
      user: payload,
      userId: payload.userId || payload.id || payload._id,
      label: payload.name || payload.phone || '该用户',
      selectedDays: 30
    });
  };

  const confirmAdminGrant = () => {
    if (!adminGrantPick) return;
    onAction('setAdmin', {
      ...adminGrantPick.user,
      userId: adminGrantPick.userId,
      adminDays: adminGrantPick.selectedDays
    });
    setAdminGrantPick(null);
  };

  const confirmModifyBan = () => {
    if (!modBanPick) return;
    onAction('modifyBanDuration', {
      userId: modBanPick.userId,
      days: modBanPick.selectedDays,
      overrideOf: modBanPick.log?.id
    });
    setModBanPick(null);
  };

  const overriddenLogIds = new Set(
    (auditLogs || []).filter(Boolean).map((log) => log.meta?.overridesLogId).filter(Boolean)
  );

  const oversightLogs = (auditLogs || []).filter((log) => {
    if (!log) return false;
    if (overriddenLogIds.has(log.id)) return false;
    if (log.meta?.actorRole === 'admin' && isAdminModerationLog(log)) return true;
    if (log.action === 'admin_ban_approve' && log.meta?.requestedByName) return true;
    return false;
  });

  const findUserByLogTarget = (log) => {
    const targetId = String(log.targetId || '');
    return usersList.find(
      (u) =>
        String(u.id) === targetId ||
        String(u.userId) === targetId ||
        String(u.uid) === targetId ||
        String(u.phone) === targetId
    );
  };

  const handleOversightRevokeBan = (log) => {
    if (!window.confirm('确定撤销该封禁操作？用户将立即解封。')) return;
    onAction('overrideRevokeBan', { log });
  };

  const handleOversightRestoreContent = (log) => {
    if (!window.confirm('确定恢复该作品展示？')) return;
    onAction('overrideRestoreContent', { log });
  };

  const handleOversightRevokeAdmin = (log) => {
    const user = findUserByLogTarget(log);
    if (!user) {
      showToast?.('未找到对应用户');
      return;
    }
    if (!window.confirm(`确定取消 ${user.name || user.phone} 的管理员权限？`)) return;
    onAction('removeAdmin', { ...user, userId: user.userId || user.id || user._id });
  };

  const bottomPanelOpen = !!(banPick || adminGrantPick || modBanPick);

  const handlePublishAnn = async () => {
    const ok = await onAction('publishAnn', {
      title: newAnnTitle,
      content: newAnnContent
    });
    if (ok) {
      setNewAnnTitle('');
      setNewAnnContent('');
      setShowAnnPreview(false);
    }
  };

  const sortedAnnouncements = [...announcements].sort((a, b) => {
    const pinDiff = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
    if (pinDiff !== 0) return pinDiff;
    return (b.pinnedAt || b.createdAt || 0) - (a.pinnedAt || a.createdAt || 0);
  });

  const contentPool = adminAllContent.length ? adminAllContent : [...posts, ...pets];
  const contentKeyword = (contentSearch || '').trim().toLowerCase();
  const filteredContent = contentPool.filter((item) => {
    const type = item.type === 'adopt' ? 'adopt' : 'post';
    if (contentTypeFilter === 'post' && type !== 'post') return false;
    if (contentTypeFilter === 'adopt' && type !== 'adopt') return false;
    const removed = isContentDeleted(item);
    if (contentStatusFilter === 'active' && removed) return false;
    if (contentStatusFilter === 'removed' && !removed) return false;
    if (contentKeyword) {
      const haystack = [
        item.title,
        item.name,
        item.user,
        item.content,
        item.description,
        item.petType
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(contentKeyword)) return false;
    }
    return true;
  });

  const auditNow = Date.now();
  const auditTodayStart = new Date();
  auditTodayStart.setHours(0, 0, 0, 0);
  const filteredAuditLogs = (auditLogs || []).filter((log) => {
    if (!log) return false;
    if (auditActionFilter !== 'all' && log.action !== auditActionFilter) return false;
    const ts = Number(log.createdAt || 0);
    if (auditTimeFilter === 'today' && ts < auditTodayStart.getTime()) return false;
    if (auditTimeFilter === '7d' && ts < auditNow - 7 * 86400000) return false;
    if (auditTimeFilter === '30d' && ts < auditNow - 30 * 86400000) return false;
    const actorQ = (auditActorSearch || '').trim().toLowerCase();
    if (actorQ) {
      const haystack = [log.actorName, log.actorPhone, log.actorId, log.actorUid]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(actorQ)) return false;
    }
    return true;
  });

  return (
    <div className="h-[100dvh] bg-stone-950 flex flex-col safe-area-top relative overflow-hidden">
      <header className="shrink-0 px-3 md:px-6 py-3 border-b border-white/5 flex items-center justify-between gap-3 bg-stone-950">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-bold text-white/70 hover:text-white transition-colors shrink-0"
          >
            <ChevronLeft size={20} />
            <span className="hidden sm:inline">返回站点</span>
          </button>
          <div className="min-w-0 flex items-center gap-2">
            <ShieldCheck size={20} className="text-amber-500 shrink-0" />
            <h2 className="text-base md:text-xl font-black text-amber-500 truncate">
              {isSuperAdmin ? '超级管理后台' : '管理后台'}
            </h2>
            {!isSuperAdmin && (
              <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full shrink-0 hidden sm:inline">
                普通管理员
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 relative">
        <aside className="yugui-admin-sidebar w-[5.25rem] md:w-52 shrink-0 border-r border-white/5 bg-zinc-950/80 flex flex-col py-2 px-1.5 md:px-2 gap-1 safe-area-bottom">
          {adminTabs.map((tab) => {
            const Icon = ADMIN_TAB_ICONS[tab] || FileText;
            const active = adminTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setAdminTab(tab)}
                className={`w-full flex flex-col md:flex-row items-center md:items-center gap-1 md:gap-2.5 px-1.5 md:px-3 py-2.5 md:py-3 rounded-xl transition-colors border ${
                  active
                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/35'
                    : 'text-white/45 hover:bg-white/5 hover:text-white border-transparent'
                }`}
              >
                <Icon size={20} className="shrink-0" strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] md:text-sm font-bold leading-tight text-center md:text-left">
                  <span className="md:hidden">{adminTabShortLabels[tab] || tab}</span>
                  <span className="hidden md:inline">{tab}</span>
                </span>
                {(tab === '举报审核' && pendingReports.length > 0) ||
                (tab === '用户反馈' && pendingFeedback.length > 0) ||
                (tab === '用户管理' && isSuperAdmin && pendingBanRequests.length > 0) ? (
                  <span
                    className={`text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none md:ml-auto ${
                      tab === '举报审核'
                        ? 'bg-red-500 text-white'
                        : tab === '用户反馈'
                          ? 'bg-amber-500 text-black'
                          : 'bg-orange-500 text-white'
                    }`}
                  >
                    {tab === '举报审核'
                      ? pendingReports.length
                      : tab === '用户反馈'
                        ? pendingFeedback.length
                        : pendingBanRequests.length}
                  </span>
                ) : null}
              </button>
            );
          })}
        </aside>

        <main className={`flex-1 min-w-0 overflow-y-auto no-scrollbar p-3 md:p-6 ${bottomPanelOpen ? 'pb-56' : 'pb-4'}`}>
        {adminTab === '数据概览' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center gap-2 text-white/50 text-xs font-bold uppercase tracking-widest">
              <BarChart3 size={14} className="text-amber-500" />
              平台概况 · {new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {dashboardStats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <button
                    key={stat.label}
                    type="button"
                    onClick={() => setAdminTab(stat.tab)}
                    className={`${stat.bg} border rounded-xl md:rounded-2xl p-3.5 md:p-5 text-left hover:brightness-110 active:scale-[0.98] transition-all min-h-[88px] md:min-h-0`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Icon size={20} className={stat.accent} />
                      <ChevronRight size={14} className="text-white/20" />
                    </div>
                    <p className={`text-2xl md:text-3xl font-black ${stat.accent}`}>{stat.value}</p>
                    <p className="text-[11px] md:text-xs text-white/50 font-bold mt-1">{stat.label}</p>
                  </button>
                );
              })}
            </div>
            {(pendingReports.length > 0 || pendingFeedback.length > 0 || (isSuperAdmin && pendingBanRequests.length > 0)) && (
              <div className="bg-zinc-900 border border-white/5 rounded-2xl p-4 md:p-6">
                <p className="text-sm font-black text-amber-500 mb-3">待办提醒</p>
                <ul className="space-y-2 text-sm text-white/70">
                  {pendingReports.length > 0 && (
                    <li className="flex items-center justify-between gap-2">
                      <span>有 {pendingReports.length} 条举报待处理</span>
                      <button type="button" onClick={() => setAdminTab('举报审核')} className="text-xs font-bold text-amber-500 shrink-0 yugui-admin-touch px-2 py-1">去处理 →</button>
                    </li>
                  )}
                  {pendingFeedback.length > 0 && (
                    <li className="flex items-center justify-between gap-2">
                      <span>有 {pendingFeedback.length} 条反馈待处理</span>
                      <button type="button" onClick={() => setAdminTab('用户反馈')} className="text-xs font-bold text-amber-500 shrink-0 yugui-admin-touch px-2 py-1">去处理 →</button>
                    </li>
                  )}
                  {isSuperAdmin && pendingBanRequests.length > 0 && (
                    <li className="flex items-center justify-between gap-2">
                      <span>有 {pendingBanRequests.length} 条封禁申请待审批</span>
                      <button type="button" onClick={() => setAdminTab('用户管理')} className="text-xs font-bold text-orange-400 shrink-0 yugui-admin-touch px-2 py-1">去审批 →</button>
                    </li>
                  )}
                </ul>
              </div>
            )}
            <p className="text-[10px] text-white/30 text-center">点击卡片可跳转到对应管理模块</p>
          </div>
        )}

        {adminTab === '举报审核' && (
          <div className="space-y-4">
            {pendingReports.length === 0 ? (
              <div className="py-20 text-center opacity-30"><p className="font-black">暂无待审核举报</p></div>
            ) : (
              <>
                <div className="sticky top-0 z-10 bg-stone-950/95 backdrop-blur-md border border-white/10 rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-3">
                  <button
                    type="button"
                    onClick={toggleSelectAllReports}
                    className="flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white yugui-admin-touch px-1"
                  >
                    {allReportsSelected ? (
                      <CheckSquare size={20} className="text-amber-500 shrink-0" />
                    ) : (
                      <Square size={20} className="text-white/40 shrink-0" />
                    )}
                    全选 ({selectedReportIds.length}/{pendingReports.length})
                  </button>
                  <div className="flex flex-col md:flex-row gap-2 md:ml-auto md:flex-1 md:justify-end">
                    <button
                      type="button"
                      disabled={!selectedReportIds.length}
                      onClick={handleBatchIgnoreReports}
                      className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-white/5 text-white/60 disabled:opacity-30`}
                    >
                      批量忽略
                    </button>
                    <button
                      type="button"
                      disabled={!selectedReportIds.length}
                      onClick={handleBatchTakedownReports}
                      className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-red-500 text-white disabled:opacity-30`}
                    >
                      批量下架
                    </button>
                  </div>
                </div>
              {pendingReports.map(report => {
                const reportId = String(report.id);
                const isSelected = selectedReportIds.includes(reportId);
                const resolved = resolveReportedContent(report, posts, pets);
                const actionPayload = {
                  ...report,
                  contentId: report.targetId,
                  contentType: report.targetType || report.type || 'post',
                  targetId: report.targetId,
                  targetType: report.targetType || report.type || 'post',
                  userId: resolved?.userId || report.userId
                };
                const reporterLabel =
                  report.reporterName ||
                  report.reporterPhone ||
                  report.reporterId ||
                  report.reporterUid ||
                  '未知';

                return (
                <div
                  key={report.id}
                  className={`bg-zinc-900 border rounded-xl md:rounded-2xl p-3.5 md:p-6 transition-colors ${
                    isSelected ? 'border-amber-500/40 ring-1 ring-amber-500/20' : 'border-white/5'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3 md:mb-4 gap-2">
                    <button
                      type="button"
                      onClick={() => toggleReportSelect(report.id)}
                      className="flex items-start gap-3 min-w-0 text-left flex-1 yugui-admin-touch"
                      aria-label={isSelected ? '取消选择' : '选择该举报'}
                    >
                      {isSelected ? (
                        <CheckSquare size={22} className="text-amber-500 shrink-0 mt-0.5" />
                      ) : (
                        <Square size={22} className="text-white/30 shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0">
                        <h3 className="font-black text-red-400 mb-1 text-sm md:text-base">举报理由：{report.reason}</h3>
                        <p className="text-[11px] md:text-xs text-white/40">举报时间：{new Date(report.createdAt).toLocaleString()}</p>
                        <p className="text-[11px] md:text-xs text-white/40 truncate">举报人：{reporterLabel}</p>
                      </div>
                    </button>
                    <span className="text-[10px] md:text-xs font-black bg-red-500/20 text-red-400 px-2.5 py-1 rounded-full shrink-0">待处理</span>
                  </div>

                  <ReportedContentPreview
                    report={report}
                    posts={posts}
                    pets={pets}
                    onView={onViewReportedContent}
                  />

                  {report.details ? (
                    <div className="bg-black/30 p-4 rounded-xl mb-4">
                      <p className="text-[10px] font-bold text-white/40 mb-1">举报补充说明</p>
                      <p className="text-sm text-white/80">{report.details}</p>
                    </div>
                  ) : null}

                  <div className="flex flex-col md:flex-row md:flex-wrap gap-2 md:gap-3">
                    <button type="button" onClick={() => onAction('deleteContent', actionPayload)} className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-red-500 text-white`}>下架违规内容</button>
                    <button
                      type="button"
                      onClick={() => openBanPicker(actionPayload, resolved?.user ? `@${resolved.user}` : actionPayload.userId)}
                      disabled={!actionPayload.userId}
                      className={`w-full md:w-auto ${ADMIN_ACTION_BTN} ${isSuperAdmin ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'} disabled:opacity-40`}
                    >
                      {isSuperAdmin ? '封禁发布账号' : '申请封禁账号'}
                    </button>
                    <button type="button" onClick={() => onAction('ignoreReport', report)} className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-white/5 text-white/60`}>忽略举报</button>
                  </div>
                </div>
              ); })}
              </>
            )}
          </div>
        )}

        {adminTab === '用户反馈' && (
          <div className="space-y-4">
            {feedbackList.length === 0 ? (
              <div className="py-20 text-center opacity-30"><p className="font-black">暂无用户反馈</p></div>
            ) : (
              feedbackList.map((item) => {
                const isPending = !item.status || item.status === 'pending';
                const submitter =
                  item.userName || item.name || item.phone || item.uid || item.userId || '未知用户';

                return (
                  <div
                    key={item.id}
                    className={`bg-zinc-900 border rounded-xl md:rounded-2xl p-3.5 md:p-6 ${isPending ? 'border-amber-500/25' : 'border-white/5 opacity-80'}`}
                  >
                    <div className="flex justify-between items-start mb-3 md:mb-4 gap-2">
                      <div className="min-w-0">
                        <h3 className="font-black text-amber-500 mb-1 text-sm md:text-base">反馈类型：{item.type || '建议'}</h3>
                        <p className="text-[11px] md:text-xs text-white/40">提交时间：{item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}</p>
                        <p className="text-[11px] md:text-xs text-white/40 truncate">用户：{submitter}</p>
                      </div>
                      <span
                        className={`text-xs font-black px-3 py-1 rounded-full shrink-0 ${
                          isPending ? 'bg-amber-500/20 text-amber-400' : 'bg-white/10 text-white/50'
                        }`}
                      >
                        {isPending ? '待处理' : '已办结'}
                      </span>
                    </div>
                    <div className="bg-black/30 p-4 rounded-xl mb-4">
                      <p className="text-sm text-white/80 whitespace-pre-wrap">{item.content}</p>
                    </div>
                    {item.adminReply ? (
                      <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl mb-4">
                        <p className="text-[10px] font-bold text-amber-500/80 mb-1">官方回复</p>
                        <p className="text-sm text-white/85 whitespace-pre-wrap">{item.adminReply}</p>
                        {item.handledAt && (
                          <p className="text-[10px] text-white/35 mt-2">
                            办结于 {new Date(item.handledAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    ) : null}
                    {isPending ? (
                      <div className="space-y-3">
                        <textarea
                          placeholder="选填：回复用户（提交后将发送到用户消息通知）"
                          value={feedbackReplyDraft[item.id] || ''}
                          onChange={(e) =>
                            setFeedbackReplyDraft((prev) => ({ ...prev, [item.id]: e.target.value }))
                          }
                          className="w-full h-24 bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white outline-none focus:border-amber-500 resize-none"
                        />
                        <div className="flex flex-col md:flex-row md:flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              onAction('handleFeedback', {
                                feedbackId: item.id,
                                reply: (feedbackReplyDraft[item.id] || '').trim()
                              })
                            }
                            className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-amber-500 text-black`}
                          >
                            {(feedbackReplyDraft[item.id] || '').trim() ? '回复并办结' : '标记已处理'}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        )}

        {adminTab === '用户管理' && (
          <div>
            {isSuperAdmin && pendingBanRequests.length > 0 && (
              <div className="mb-5 md:mb-6 bg-orange-500/10 border border-orange-500/25 rounded-xl md:rounded-2xl p-3.5 md:p-5 space-y-3">
                <h3 className="text-sm font-black text-orange-400">待审批封禁申请 ({pendingBanRequests.length})</h3>
                {pendingBanRequests.map((req) => (
                  <div key={req.id} className="bg-zinc-900/80 border border-white/5 rounded-xl p-3.5 flex flex-col gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white">
                        {req.targetName || req.targetPhone || req.targetUserId || '未知用户'}
                        <span className="text-orange-400 ml-2">· {formatBanDaysLabel(req.days)}</span>
                      </p>
                      <p className="text-[11px] text-white/40 mt-1">
                        申请人：{req.requestedByName || req.requestedByPhone || '管理员'}
                        {req.createdAt ? ` · ${new Date(req.createdAt).toLocaleString()}` : ''}
                      </p>
                      {req.reason ? (
                        <p className="text-xs text-white/55 mt-1">理由：{req.reason}</p>
                      ) : null}
                    </div>
                    <div className="flex flex-col md:flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => onAction('approveBanRequest', req)}
                        className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-red-600 text-white`}
                      >
                        批准封禁
                      </button>
                      <button
                        type="button"
                        onClick={() => onAction('rejectBanRequest', req)}
                        className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-white/5 text-white/60`}
                      >
                        驳回
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mb-4 md:mb-6 flex items-center gap-4">
              <input type="text" placeholder="搜索 UID / 手机号 / 用户文档 ID" value={searchUid} onChange={e=>setSearchUid(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 md:py-3 text-sm text-white outline-none focus:border-amber-500 min-h-[44px] md:min-h-0" />
            </div>
            <div className="space-y-3 md:space-y-4">
              {filteredUsers.map(user => (
                <div key={user.id} className="bg-zinc-900 border border-white/5 rounded-xl md:rounded-2xl p-3.5 md:p-6 flex flex-col gap-3 md:gap-4">
                  <button
                    type="button"
                    onClick={() => setDetailUser(user)}
                    className="flex items-center gap-3 md:gap-4 min-w-0 text-left w-full hover:opacity-90 transition-opacity yugui-admin-touch"
                  >
                    <img src={user.avatar || DEFAULT_AVATAR} className="w-11 h-11 md:w-12 md:h-12 rounded-full shrink-0" alt="" />
                    <div className="min-w-0 flex-1">
                      <p className="font-bold flex items-center flex-wrap gap-1 text-sm md:text-base">
                        {user.name}
                        {user.role === 'super_admin' && <BadgeCheck size={14} className="text-amber-500" />}
                        {user.role === 'admin' && <ShieldPlus size={14} className="text-blue-400" />}
                        {user.isBanned && (
                          <span className="text-[10px] font-black bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">已封禁</span>
                        )}
                      </p>
                      <p className="text-[11px] md:text-xs text-white/40 truncate">UID: {user.uid} | 手机号: {user.phone}</p>
                      {user.isBanned && formatBanStatus(user) && (
                        <p className="text-[10px] text-red-400/80 mt-1">{formatBanStatus(user)}</p>
                      )}
                      {user.role === 'admin' && formatAdminRoleStatus(user) && (
                        <p className="text-[10px] text-blue-400/80 mt-1">{formatAdminRoleStatus(user)}</p>
                      )}
                    </div>
                    <ChevronRight size={18} className="text-white/25 shrink-0" />
                  </button>
                  <div className="flex flex-col md:flex-row md:flex-wrap gap-2 md:gap-3 shrink-0 md:ml-auto">
                    {user.role !== 'super_admin' && String(user.phone || '') !== OFFICIAL_ADMIN_PHONE && (
                      <>
                        {isSuperAdmin && (
                          <button type="button" onClick={() => (user.role === 'admin' ? onAction('removeAdmin', { ...user, userId: user.userId || user.id || user._id }) : openAdminGrantPicker({ ...user, userId: user.userId || user.id || user._id }))} className={`w-full md:w-auto ${ADMIN_ACTION_BTN} ${user.role === 'admin' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-500 text-white'}`}>
                            {user.role === 'admin' ? '取消管理员' : '设为管理员'}
                          </button>
                        )}
                        {isSuperAdmin ? (
                          <button
                            type="button"
                            onClick={() => (user.isBanned
                              ? onAction('unbanUser', { ...user, userId: user.userId || user.id || user._id })
                              : openBanPicker({ ...user, userId: user.userId || user.id || user._id }, user.name || user.phone)
                            )}
                            className={`w-full md:w-auto ${ADMIN_ACTION_BTN} ${user.isBanned ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
                          >
                            {user.isBanned ? '解封账号' : '封禁账号'}
                          </button>
                        ) : !user.isBanned ? (
                          <button
                            type="button"
                            onClick={() => openBanPicker({ ...user, userId: user.userId || user.id || user._id }, user.name || user.phone)}
                            className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-orange-500 text-white`}
                          >
                            申请封禁
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {adminTab === '管理员监管' && isSuperAdmin && (
          <div className="space-y-4 max-w-4xl">
            <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-4">
              <p className="text-sm font-black text-amber-400 mb-1">普通管理员操作监管</p>
              <p className="text-xs text-white/50 leading-relaxed">
                此处汇总普通管理员的封禁申请、内容下架等操作。超级管理员可撤销封禁、恢复作品、调整封禁时长或收回管理员权限。
              </p>
            </div>

            <p className="text-xs text-white/40">
              共 {oversightLogs.length} 条可监管记录
              {overriddenLogIds.size > 0 ? ` · 已撤销/覆盖 ${overriddenLogIds.size} 条` : ''}
            </p>

            {oversightLogs.length === 0 ? (
              <div className="py-16 text-center opacity-30">
                <ShieldPlus size={40} className="mx-auto mb-3 opacity-50" />
                <p className="font-black">暂无普通管理员操作记录</p>
                <p className="text-xs mt-2">普通管理员的下架、封禁申请等操作会出现在这里</p>
              </div>
            ) : (
              oversightLogs.map((log) => {
                const banLabel = formatAuditBanLabel(log.meta);
                const grantLabel = formatAdminGrantLabel(log.meta);
                const isBanAction =
                  log.action === 'admin_ban' ||
                  log.action === 'admin_ban_approve' ||
                  log.action === 'admin_ban_request';
                const isTakedown = log.action === 'admin_takedown';
                const isGrant = log.action === 'admin_grant_role';
                const actorLabel =
                  log.meta?.actorRole === 'admin'
                    ? log.actorName || log.actorPhone || '普通管理员'
                    : log.meta?.requestedByName || log.meta?.requestedByPhone || log.actorName || '—';
                const targetUser = isBanAction || isGrant ? findUserByLogTarget(log) : null;
                const userStillBanned = targetUser?.isBanned;

                return (
                  <div
                    key={log.id || `${log.createdAt}_${log.action}`}
                    className="bg-zinc-900 border border-white/5 rounded-xl p-4 space-y-3"
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <span className="text-xs font-black text-amber-500">
                          {getAuditActionLabel(log.action)}
                        </span>
                        {banLabel && (
                          <span className="text-xs font-bold text-red-400 ml-2">{banLabel}</span>
                        )}
                        {grantLabel && (
                          <span className="text-xs font-bold text-blue-400 ml-2">{grantLabel}</span>
                        )}
                      </div>
                      <span className="text-[10px] text-white/30 shrink-0">
                        {log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}
                      </span>
                    </div>
                    <p className="text-sm text-white/80">{log.summary || '—'}</p>
                    <p className="text-[10px] text-white/40">
                      操作者：{actorLabel}
                      {log.meta?.requestedByName && log.action === 'admin_ban_approve'
                        ? `（申请人为 ${log.meta.requestedByName}）`
                        : ''}
                      {log.targetId ? ` · 对象：${log.targetType || '—'}/${log.targetId}` : ''}
                    </p>

                    <div className="flex flex-col md:flex-row flex-wrap gap-2 pt-1">
                      {isBanAction && userStillBanned && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleOversightRevokeBan(log)}
                            className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-green-600 text-white`}
                          >
                            撤销封禁
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setModBanPick({
                                log,
                                userId: log.targetId,
                                selectedDays: log.meta?.banDays ?? 7
                              })
                            }
                            className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-orange-500 text-white`}
                          >
                            调整封禁时长
                          </button>
                        </>
                      )}
                      {isBanAction && !userStillBanned && (
                        <span className="text-xs text-white/35 py-2">该用户当前未处于封禁状态</span>
                      )}
                      {isTakedown && (
                        <button
                          type="button"
                          onClick={() => handleOversightRestoreContent(log)}
                          className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-emerald-600 text-white`}
                        >
                          恢复作品
                        </button>
                      )}
                      {isGrant && targetUser?.role === 'admin' && (
                        <button
                          type="button"
                          onClick={() => handleOversightRevokeAdmin(log)}
                          className={`w-full md:w-auto ${ADMIN_ACTION_BTN} bg-blue-500/20 text-blue-400`}
                        >
                          收回管理员权限
                        </button>
                      )}
                      {log.action === 'admin_ignore_report' && (
                        <span className="text-xs text-white/35 py-2">已忽略举报（仅记录，无需撤销）</span>
                      )}
                      {log.action === 'admin_ban_request' && (
                        <span className="text-xs text-white/35 py-2">封禁申请 · 请在「用户管理」中审批或驳回</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {adminTab === '公告发布' && isSuperAdmin && (
          <div className="max-w-2xl mx-auto space-y-8">
            <div className="space-y-4">
              <h3 className="text-sm font-black text-amber-500 uppercase tracking-widest">发布新公告</h3>
              <input
                type="text"
                placeholder="公告标题"
                value={newAnnTitle}
                onChange={(e) => setNewAnnTitle(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-lg font-bold text-white outline-none focus:border-amber-500"
              />
              <textarea
                placeholder="公告正文"
                value={newAnnContent}
                onChange={(e) => setNewAnnContent(e.target.value)}
                className="w-full h-40 bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-white outline-none focus:border-amber-500 resize-none"
              />
              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setShowAnnPreview((v) => !v)}
                  disabled={!newAnnTitle.trim() && !newAnnContent.trim()}
                  className="flex-1 min-w-0 sm:min-w-[120px] min-h-[44px] py-3 rounded-xl bg-white/5 text-white/70 font-bold text-sm border border-white/10 disabled:opacity-30"
                >
                  {showAnnPreview ? '收起预览' : '预览效果'}
                </button>
                <button
                  type="button"
                  onClick={handlePublishAnn}
                  disabled={!newAnnTitle.trim() || !newAnnContent.trim()}
                  className="flex-1 min-w-0 sm:min-w-[120px] min-h-[44px] py-3 bg-amber-500 text-black font-black rounded-xl disabled:opacity-30 hover:bg-amber-400"
                >
                  发布全站公告
                </button>
              </div>
              {showAnnPreview && (newAnnTitle.trim() || newAnnContent.trim()) && (
                <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10">
                  <p className="text-[10px] font-bold text-amber-500/80 mb-3 uppercase tracking-widest">用户消息中的预览</p>
                  <div className="flex items-start gap-3">
                    <Megaphone size={20} className="text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-white mb-1">{newAnnTitle.trim() || '（无标题）'}</h4>
                      <p className="text-sm text-white/80 whitespace-pre-wrap">{newAnnContent.trim() || '（无正文）'}</p>
                      <p className="text-xs text-white/40 mt-2">刚刚 · 平台公告</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-black text-white/50 uppercase tracking-widest">
                历史公告 ({sortedAnnouncements.length})
              </h3>
              {sortedAnnouncements.length === 0 ? (
                <div className="py-12 text-center opacity-30">
                  <p className="font-black">暂无历史公告</p>
                </div>
              ) : (
                sortedAnnouncements.map((ann) => (
                  <div
                    key={ann.id}
                    className={`bg-zinc-900 border rounded-2xl p-4 md:p-5 ${
                      ann.withdrawn
                        ? 'border-white/10 opacity-60'
                        : ann.pinned
                          ? 'border-amber-500/40'
                          : 'border-white/5'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-3 mb-3">
                      <div className="min-w-0">
                        <p className="font-black text-white flex items-center gap-2 flex-wrap">
                          {ann.title}
                          {ann.withdrawn && (
                            <span className="text-[10px] font-black bg-white/10 text-white/50 px-2 py-0.5 rounded-full">
                              已撤回
                            </span>
                          )}
                          {!ann.withdrawn && ann.pinned && (
                            <span className="text-[10px] font-black bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                              <Pin size={10} /> 置顶
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-white/40 mt-1">
                          {ann.createdAt ? new Date(ann.createdAt).toLocaleString() : '—'}
                          {ann.withdrawn && ann.withdrawnAt && (
                            <span className="ml-2 text-white/30">
                              · 撤回于 {new Date(ann.withdrawnAt).toLocaleString('zh-CN')}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-white/75 whitespace-pre-wrap mb-4 line-clamp-4">{ann.content}</p>
                    {!ann.withdrawn && (
                      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            onAction('pinAnn', { annId: ann.id, id: ann.id, pin: !ann.pinned })
                          }
                          className={`w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2.5 sm:py-1.5 text-xs font-bold rounded-xl border ${
                            ann.pinned
                              ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                              : 'bg-white/5 text-white/60 border-white/10'
                          }`}
                        >
                          {ann.pinned ? '取消置顶' : '置顶'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`确定撤回公告「${ann.title}」？\n撤回后全站用户将不再看到，但会保留在官方公告日志中。`)) {
                              onAction('withdrawAnn', { annId: ann.id, id: ann.id, title: ann.title });
                            }
                          }}
                          className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2.5 sm:py-1.5 text-xs font-bold rounded-xl bg-red-500/15 text-red-400 border border-red-500/25"
                        >
                          撤回公告
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {adminTab === '网站维护' && isSuperAdmin && (
          <div className="max-w-2xl mx-auto space-y-8">
            <div
              className={`rounded-2xl border p-5 md:p-6 ${
                isMaintenanceActive(siteMaintenance)
                  ? 'bg-amber-500/10 border-amber-500/40'
                  : 'bg-zinc-900 border-white/10'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm font-black text-white flex items-center gap-2">
                    <Power size={18} className={isMaintenanceActive(siteMaintenance) ? 'text-amber-500' : 'text-white/40'} />
                    {isMaintenanceActive(siteMaintenance) ? '维护模式运行中' : '网站正常运行'}
                  </p>
                  <p className="text-xs text-white/45 mt-2 leading-relaxed">
                    开启后普通用户无法使用网站（含登录、浏览、发布）；普通管理员与超级管理员可照常使用。
                  </p>
                  {isMaintenanceActive(siteMaintenance) && (
                    <p className="text-xs text-amber-400/90 mt-2 font-bold">
                      当前维护窗口：{formatMaintenanceWindow(siteMaintenance)}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  disabled={maintSaving}
                  onClick={handleQuickToggleMaintenance}
                  className={`shrink-0 min-h-[44px] px-5 py-2.5 rounded-xl text-sm font-black transition-colors disabled:opacity-40 ${
                    maintEnabled
                      ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                      : 'bg-red-600 text-white hover:bg-red-500'
                  }`}
                >
                  {maintEnabled ? '立即恢复网站' : '暂停网站（维护）'}
                </button>
              </div>
            </div>

            <div className="space-y-5">
              <h3 className="text-sm font-black text-amber-500 uppercase tracking-widest">维护说明（用户可见）</h3>
              <input
                type="text"
                placeholder="公告标题，如：网站维护中"
                value={maintTitle}
                onChange={(e) => setMaintTitle(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-lg font-bold text-white outline-none focus:border-amber-500"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block space-y-2">
                  <span className="text-xs font-bold text-white/50">维护开始时间</span>
                  <input
                    type="datetime-local"
                    value={maintStart}
                    onChange={(e) => setMaintStart(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-amber-500 [color-scheme:dark]"
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-bold text-white/50">预计恢复时间</span>
                  <input
                    type="datetime-local"
                    value={maintEnd}
                    onChange={(e) => setMaintEnd(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-amber-500 [color-scheme:dark]"
                  />
                </label>
              </div>
              <textarea
                placeholder="可选：自定义向用户展示的说明。留空则使用系统默认维护公告（含上述时间段）。"
                value={maintMessage}
                onChange={(e) => setMaintMessage(e.target.value)}
                className="w-full h-36 bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-white outline-none focus:border-amber-500 resize-none"
              />
              <div className="p-4 rounded-xl border border-white/10 bg-black/30">
                <p className="text-[10px] font-bold text-white/40 mb-3 uppercase tracking-widest">用户将看到</p>
                <p className="font-black text-white mb-1">{maintTitle.trim() || '网站维护中'}</p>
                <p className="text-xs text-amber-400/80 mb-2">
                  维护时间：{formatMaintenanceWindow({
                    startAt: fromDatetimeLocalValue(maintStart),
                    endAt: fromDatetimeLocalValue(maintEnd)
                  })}
                </p>
                <p className="text-sm text-white/75 whitespace-pre-wrap line-clamp-6">
                  {maintMessage.trim() ||
                    `很抱歉，予归平台正在进行系统维护与功能升级，期间暂时无法为普通用户提供服务。\n\n维护时间：${formatMaintenanceWindow({
                      startAt: fromDatetimeLocalValue(maintStart),
                      endAt: fromDatetimeLocalValue(maintEnd)
                    })}\n\n感谢您的理解与耐心等待，维护完成后将第一时间恢复访问。`}
                </p>
              </div>
              <button
                type="button"
                disabled={maintSaving}
                onClick={handleSaveMaintenance}
                className="w-full min-h-[48px] py-3 bg-amber-500 text-black font-black rounded-xl disabled:opacity-40 hover:bg-amber-400"
              >
                {maintSaving ? '保存中…' : '保存维护设置'}
              </button>
            </div>
          </div>
        )}

        {adminTab === '内容管理' && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
              <h3 className="text-lg font-black">内容管理</h3>
              <p className="text-xs text-white/40">
                共 {contentPool.length} 条 · 当前显示 {filteredContent.length} 条
              </p>
            </div>

            <div className="flex flex-col gap-3 mb-4">
              <input
                type="search"
                placeholder="搜索标题 / 发布者 / 正文关键词"
                value={contentSearch}
                onChange={(e) => setContentSearch(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-amber-500"
              />
              <div className="flex flex-wrap gap-2 pb-1">
                {[
                  { id: 'all', label: '全部类型' },
                  { id: 'post', label: '日常作品' },
                  { id: 'adopt', label: '领养信息' }
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setContentTypeFilter(opt.id)}
                    className={`${ADMIN_FILTER_PILL} ${
                      contentTypeFilter === opt.id
                        ? 'bg-amber-500 text-black border-amber-500'
                        : 'bg-white/5 text-white/60 border-white/10'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 pb-1">
                {[
                  { id: 'all', label: '全部状态' },
                  { id: 'active', label: '正常展示' },
                  { id: 'removed', label: '已下架' }
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setContentStatusFilter(opt.id)}
                    className={`${ADMIN_FILTER_PILL} ${
                      contentStatusFilter === opt.id
                        ? 'bg-white/15 text-white border-white/25'
                        : 'bg-white/5 text-white/60 border-white/10'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredContent.length === 0 ? (
              <div className="py-16 text-center opacity-30">
                <p className="font-black">没有符合筛选条件的内容</p>
              </div>
            ) : (
              filteredContent.map((item) => {
                const removed = isContentDeleted(item);
                const typeLabel = item.type === 'adopt' ? '领养信息' : '日常作品';
                return (
                  <div
                    key={`${item.type}_${item.id}`}
                    className={`bg-zinc-900 border rounded-xl md:rounded-2xl p-3.5 md:p-6 flex flex-col gap-3 md:gap-4 ${
                      removed ? 'border-red-500/20 opacity-75' : 'border-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3 md:gap-4 min-w-0">
                      {item.img ? (
                        <img src={item.img} className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-cover shrink-0" alt="" />
                      ) : (
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-white/5 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-bold truncate flex items-center gap-2 flex-wrap">
                          {item.title || item.name || '无标题'}
                          {removed && (
                            <span className="text-[10px] font-black bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
                              已下架
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-white/40 truncate">
                          发布者: {item.user || '未知'} | 类型: {typeLabel}
                        </p>
                        {(item.content || item.description) && (
                          <p className="text-xs text-white/35 mt-1 line-clamp-2">
                            {item.content || item.description}
                          </p>
                        )}
                      </div>
                    </div>
                    {!removed ? (
                      <button
                        type="button"
                        onClick={() =>
                          onAction('deleteContent', {
                            contentId: item.id,
                            contentType: item.type === 'adopt' ? 'adopt' : 'post'
                          })
                        }
                        className={`w-full md:w-auto md:self-center ${ADMIN_ACTION_BTN} bg-red-500 text-white shrink-0`}
                      >
                        下架内容
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          onAction('restoreContent', {
                            contentId: item.id,
                            contentType: item.type === 'adopt' ? 'adopt' : 'post'
                          })
                        }
                        className={`w-full md:w-auto md:self-center ${ADMIN_ACTION_BTN} bg-emerald-600 text-white shrink-0`}
                      >
                        恢复展示
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {adminTab === '操作日志' && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <p className="text-xs text-white/40">
                依法留存不少于 6 个月（常规日志默认 12 个月）；此处最多显示最近 120 条
              </p>
              <p className="text-xs text-white/50 font-bold">
                当前 {filteredAuditLogs.length} / {auditLogs.length} 条
              </p>
            </div>

            <input
              type="search"
              placeholder="搜索操作者（昵称 / 手机号 / ID）"
              value={auditActorSearch}
              onChange={(e) => setAuditActorSearch(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-amber-500"
            />

            <div className="flex flex-wrap gap-2 pb-1">
              <button
                type="button"
                onClick={() => setAuditActionFilter('all')}
                className={`${ADMIN_FILTER_PILL} ${
                  auditActionFilter === 'all'
                    ? 'bg-amber-500 text-black border-amber-500'
                    : 'bg-white/5 text-white/60 border-white/10'
                }`}
              >
                全部类型
              </button>
              {Object.entries(AUDIT_ACTION_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setAuditActionFilter(key)}
                  className={`${ADMIN_FILTER_PILL} ${
                    auditActionFilter === key
                      ? 'bg-amber-500 text-black border-amber-500'
                      : 'bg-white/5 text-white/60 border-white/10'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 pb-1">
              {[
                { id: 'all', label: '全部时间' },
                { id: 'today', label: '今天' },
                { id: '7d', label: '近 7 天' },
                { id: '30d', label: '近 30 天' }
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setAuditTimeFilter(opt.id)}
                  className={`${ADMIN_FILTER_PILL} ${
                    auditTimeFilter === opt.id
                      ? 'bg-white/15 text-white border-white/25'
                      : 'bg-white/5 text-white/60 border-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {filteredAuditLogs.length === 0 ? (
              <div className="py-16 text-center opacity-30">
                <p className="font-black">{auditLogs.length === 0 ? '暂无操作日志' : '没有符合筛选条件的日志'}</p>
                {auditLogs.length === 0 && (
                  <p className="text-xs mt-2">日志优先从服务器读取；CloudBase 为空不影响合规记录</p>
                )}
              </div>
            ) : (
              filteredAuditLogs.map((log) => (
                <div key={log.id || `${log.createdAt}_${log.action}`} className="bg-zinc-900 border border-white/5 rounded-xl p-4">
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <span className="text-xs font-black text-amber-500">
                      {getAuditActionLabel(log.action)}
                    </span>
                    <span className="text-[10px] text-white/30 shrink-0">
                      {log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}
                    </span>
                  </div>
                  <p className="text-sm text-white/80">{log.summary || '—'}</p>
                  <p className="text-[10px] text-white/40 mt-1">
                    操作者：{log.actorName || log.actorPhone || log.actorId || '未知'}
                    {log.targetId ? ` · 对象：${log.targetType || '—'}/${log.targetId}` : ''}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
        </main>
      </div>

      {banPick && (
        <BanDurationPanel
          targetLabel={banPick.label}
          selectedDays={banPick.selectedDays}
          onSelectDays={(days) => setBanPick((prev) => (prev ? { ...prev, selectedDays: days } : null))}
          onConfirm={confirmBan}
          onCancel={() => setBanPick(null)}
          requestMode={!isSuperAdmin}
        />
      )}

      {adminGrantPick && (
        <AdminGrantPanel
          targetLabel={adminGrantPick.label}
          selectedDays={adminGrantPick.selectedDays}
          onSelectDays={(days) => setAdminGrantPick((prev) => (prev ? { ...prev, selectedDays: days } : null))}
          onConfirm={confirmAdminGrant}
          onCancel={() => setAdminGrantPick(null)}
        />
      )}

      {modBanPick && (
        <BanDurationPanel
          targetLabel={modBanPick.log?.meta?.targetName || modBanPick.userId || '该用户'}
          selectedDays={modBanPick.selectedDays}
          onSelectDays={(days) => setModBanPick((prev) => (prev ? { ...prev, selectedDays: days } : null))}
          onConfirm={confirmModifyBan}
          onCancel={() => setModBanPick(null)}
          requestMode={false}
        />
      )}

      {detailUser && (
        <AdminUserDetailPanel
          user={detailUser}
          contentPool={contentPool}
          reports={reports}
          feedbackList={feedbackList}
          isSuperAdmin={isSuperAdmin}
          onBack={() => setDetailUser(null)}
          onAction={onAction}
          openBanPicker={openBanPicker}
          openAdminGrantPicker={openAdminGrantPicker}
          onViewReportedContent={(content) => {
            setDetailUser(null);
            onViewReportedContent?.(content);
          }}
        />
      )}
    </div>
  );
};

export const EmailNotificationModal = ({ notifications, announcements, userProfile, onClose, showToast }) => {
  const [activeTab, setActiveTab] = useState('all');

  const myIds = [
    userProfile?.id,
    userProfile?.uid,
    userProfile?.phone
  ]
    .filter(Boolean)
    .map((v) => String(v));

  const isForMe = (notification) => {
    if (!notification?.targetUserId && !notification?.targetPhone) return true;
    if (myIds.length === 0) return false;
    return (
      myIds.includes(String(notification.targetUserId || '')) ||
      myIds.includes(String(notification.targetPhone || ''))
    );
  };

  const personalNotifications = (notifications || []).filter(isForMe);
  
  // 合并并分类通知（已撤回公告不对用户展示）
  const visibleAnnouncements = (announcements || []).filter((ann) => !ann.withdrawn && !ann.archived);
  const combinedNotifications = [
    // 平台公告
    ...visibleAnnouncements.map(ann => ({
      ...ann,
      type: 'announcement',
      read: false
    })),
    // 用户互动通知
    ...personalNotifications
  ].sort((a, b) => b.createdAt - a.createdAt);
  
  // 根据标签筛选通知
  const filteredNotifications = activeTab === 'all' 
    ? combinedNotifications 
    : combinedNotifications.filter(notification => notification.type === activeTab);
  
  // 标记所有通知为已读
  const markAllAsRead = () => {
    showToast('所有通知已标记为已读');
  };
  
  // 获取通知类型的图标
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'announcement':
        return <Megaphone size={20} className="text-amber-500" />;
      case 'like':
        return <Heart size={20} className="text-red-500" />;
      case 'bookmark':
        return <Bookmark size={20} className="text-amber-500" />;
      case 'follow':
        return <UserPlus size={20} className="text-blue-500" />;
      case 'feedback_reply':
        return <MessageSquare size={20} className="text-amber-500" />;
      default:
        return <MessageSquare size={20} className="text-white/60" />;
    }
  };

  return (
    <div className="fixed inset-0 z-[4000] bg-stone-950 flex flex-col p-8 animate-slide-up">
      <header className="pt-10 pb-6 flex items-center justify-between border-b border-white/10">
        <button onClick={onClose} className="p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors"><ChevronLeft size={28}/></button>
        <h2 className="flex-1 text-center font-black text-sm text-amber-500 uppercase tracking-widest">消息通知</h2>
        <button onClick={markAllAsRead} className="p-2 text-amber-500 text-sm font-bold hover:bg-amber-500/10 rounded-full transition-colors">
          全部已读
        </button>
      </header>
      
      {/* 标签切换 */}
      <div className="flex border-b border-white/10">
        <button 
          onClick={() => setActiveTab('all')}
          className={`flex-1 py-4 text-sm font-bold transition-colors ${activeTab === 'all' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-white/40 hover:text-white'}`}
        >
          全部
        </button>
        <button 
          onClick={() => setActiveTab('announcement')}
          className={`flex-1 py-4 text-sm font-bold transition-colors ${activeTab === 'announcement' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-white/40 hover:text-white'}`}
        >
          平台公告
        </button>
        <button 
          onClick={() => setActiveTab('feedback_reply')}
          className={`flex-1 py-4 text-sm font-bold transition-colors ${activeTab === 'feedback_reply' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-white/40 hover:text-white'}`}
        >
          官方
        </button>
        <button 
          onClick={() => setActiveTab('like')}
          className={`flex-1 py-4 text-sm font-bold transition-colors ${activeTab === 'like' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-white/40 hover:text-white'}`}
        >
          点赞
        </button>
        <button 
          onClick={() => setActiveTab('follow')}
          className={`flex-1 py-4 text-sm font-bold transition-colors ${activeTab === 'follow' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-white/40 hover:text-white'}`}
        >
          关注
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto no-scrollbar py-6 space-y-4">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification, idx) => (
            <div key={idx} className={`p-4 rounded-xl border ${notification.read ? 'bg-white/5 border-white/5' : 'bg-amber-500/10 border-amber-500/30'}`}>
              <div className="flex items-start gap-4">
                <div className="mt-1">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold mb-1">{notification.title || notification.type}</h3>
                  <p className="text-sm text-white/80 mb-2">{notification.content}</p>
                  <p className="text-xs text-white/40">{new Date(notification.createdAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="py-20 flex flex-col items-center justify-center opacity-30">
            <MessageSquare size={48} className="mb-4"/>
            <p className="text-sm font-black uppercase">暂无通知</p>
          </div>
        )}
      </div>
    </div>
  );
};

// 账号设置模态框
export const SettingsModal = ({
  userProfile,
  onClose,
  onUpdateProfile,
  onChangePassword,
  onSendSmsCode,
  onToggleLocationAutoDetect,
  showToast,
  onShowPrivacy,
  onShowAgreement,
  onShowConvention
}) => {
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [changeCode, setChangeCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);
  const [codeTimer, setCodeTimer] = useState(0);

  const [profile, setProfile] = useState({
    name: userProfile?.name || '',
    bio: userProfile?.bio || '',
    avatar: userProfile?.avatar || '',
    coverImg: userProfile?.coverImg || '',
    location: userProfile?.location || '',
    city: userProfile?.city || '',
    province: userProfile?.province || '',
    locationData: userProfile?.locationData || null,
    locationSource: userProfile?.locationSource || '',
    locationAgreed: !!userProfile?.locationAgreed,
    showLikes: true,
    showBookmarks: true,
    showFollowing: true
  });
  const [saving, setSaving] = useState(false);

  // 处理图片上传
  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      showToast('请上传图片文件');
      return;
    }
    
    // 检查文件大小（限制为5MB）
    if (file.size > 5 * 1024 * 1024) {
      showToast('图片大小不能超过5MB');
      return;
    }
    
    // 模拟图片上传（实际项目中应该上传到服务器）
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target.result;
      if (type === 'avatar') {
        setProfile({...profile, avatar: imageUrl});
      } else if (type === 'cover') {
        setProfile({...profile, coverImg: imageUrl});
      }
      showToast(`${type === 'avatar' ? '头像' : '背景'}上传成功！`);
    };
    reader.readAsDataURL(file);
  };

  const handleSendChangeCode = async () => {
    const phone = userProfile?.phone;
    if (!phone) return showToast('无法获取绑定手机号');
    if (typeof onSendSmsCode === 'function') {
      const ok = await onSendSmsCode(phone, 'change');
      if (!ok) return;
    } else {
      showToast(`验证码已发送（内测固定码 ${DEV_SMS_CODE}）`);
    }
    setCodeTimer(60);
    const int = setInterval(() => setCodeTimer((p) => (p <= 1 ? (clearInterval(int), 0) : p - 1)), 1000);
  };

  const handleChangePasswordSubmit = async () => {
    if (changingPwd) return;
    if (!changeCode) return showToast('请输入验证码');
    if (!newPassword || newPassword.length < 6) return showToast('新密码至少6位');
    if (newPassword !== confirmNewPassword) return showToast('两次输入的新密码不一致');
    if (typeof onChangePassword !== 'function') return showToast('密码修改服务暂不可用');

    setChangingPwd(true);
    try {
      await onChangePassword({ code: changeCode, newPassword });
      showToast('密码修改成功，下次登录请使用新密码');
      setShowChangePwd(false);
      setChangeCode('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      showToast(error?.message || '密码修改失败');
    } finally {
      setChangingPwd(false);
    }
  };

  const handleUpdate = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onUpdateProfile(profile);
      showToast('个人资料更新成功！');
      onClose();
    } catch (error) {
      console.warn('资料保存失败:', error);
      showToast('资料保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-fade-in">
      <div className="bg-zinc-900 rounded-[2rem] w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        <header className="px-8 py-6 border-b border-white/5 flex justify-between items-center">
          <h2 className="text-xl font-black text-amber-500">账号设置</h2>
          <button onClick={onClose} className="p-2 bg-white/5 rounded-full hover:bg-white/10 transition-colors"><X size={24}/></button>
        </header>
        
        <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-8">
          {/* 基本信息 */}
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-white/40 mb-4">基本信息</h3>
            <div className="space-y-4">
              {/* 头像和背景图片上传 */}
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center space-x-6">
                  {/* 头像上传 */}
                  <div className="flex flex-col items-center">
                    <div className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-white/10 mb-2 cursor-pointer">
                      <img src={profile.avatar || DEFAULT_AVATAR} className="w-full h-full object-cover" alt="avatar" />
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <UploadCloud size={24} className="text-white" />
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleImageUpload(e, 'avatar')}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                    <span className="text-xs text-white/60">修改头像</span>
                  </div>
                  
                  {/* 背景图片上传 */}
                  <div className="flex-1">
                    <label className="block text-xs font-bold mb-2 text-white/60">背景图片</label>
                    <div className="relative h-24 rounded-xl overflow-hidden border-2 border-dashed border-white/10 cursor-pointer">
                      <img src={profile.coverImg || DEFAULT_COVER} className="w-full h-full object-cover" alt="cover" />
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <UploadCloud size={24} className="text-white" />
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleImageUpload(e, 'cover')}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4">
                <label className="block text-xs font-bold mb-2 text-white/60">用户名</label>
                <input 
                  type="text" 
                  value={profile.name} 
                  onChange={(e) => setProfile({...profile, name: e.target.value})}
                  className="w-full bg-transparent border border-white/10 rounded-lg p-3 text-white outline-none focus:border-amber-500"
                />
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <label className="block text-xs font-bold mb-2 text-white/60">个人简介</label>
                <textarea 
                  value={profile.bio} 
                  onChange={(e) => setProfile({...profile, bio: e.target.value})}
                  className="w-full bg-transparent border border-white/10 rounded-lg p-3 text-white outline-none focus:border-amber-500 min-h-[100px] resize-none"
                />
              </div>
            </div>
          </div>

          {/* 功能设置 */}
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-white/40 mb-4">功能设置</h3>
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4 flex justify-between items-center gap-4">
                <div className="min-w-0">
                  <span className="text-sm font-medium">随时自动定位</span>
                  <p className="text-[10px] text-white/40 mt-1 leading-relaxed">
                    开启后每次打开应用会自动更新城市；关闭后保留已保存城市
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={!!profile.locationAgreed}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setProfile((prev) => setAutoLocationConsent(prev, checked));
                      onToggleLocationAutoDetect?.(checked);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500" />
                </label>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <label className="block text-xs font-bold mb-2 text-white/60">所在城市</label>
                <LocationPicker
                  value={profile}
                  onChange={(locationFields) => setProfile((prev) => ({ ...prev, ...locationFields }))}
                  showToast={showToast}
                />
              </div>
            </div>
          </div>
          
          {/* 隐私设置 */}
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-white/40 mb-4">隐私设置</h3>
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4 flex justify-between items-center">
                <span className="text-sm font-medium">显示我的点赞</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={profile.showLikes} onChange={(e) => setProfile({...profile, showLikes: e.target.checked})} className="sr-only peer"/>
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>
              <div className="bg-white/5 rounded-xl p-4 flex justify-between items-center">
                <span className="text-sm font-medium">显示我的收藏</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={profile.showBookmarks} onChange={(e) => setProfile({...profile, showBookmarks: e.target.checked})} className="sr-only peer"/>
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>
              <div className="bg-white/5 rounded-xl p-4 flex justify-between items-center">
                <span className="text-sm font-medium">显示我的关注</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={profile.showFollowing} onChange={(e) => setProfile({...profile, showFollowing: e.target.checked})} className="sr-only peer"/>
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>
            </div>
          </div>
          
          {/* 平台规范与法律文件 */}
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-white/40 mb-4">平台规范与法律文件</h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => onShowConvention?.()}
                className="w-full bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between text-left hover:bg-amber-500/15 transition-colors"
              >
                <span className="text-sm font-black text-amber-500 flex items-center gap-2">
                  <ShieldCheck size={16} /> 予归平台公约
                </span>
                <ChevronRight size={18} className="text-amber-500/60" />
              </button>
              <button
                type="button"
                onClick={() => onShowPrivacy?.()}
                className="w-full bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between text-left hover:bg-white/10 transition-colors"
              >
                <span className="text-sm font-medium text-white/80">隐私政策</span>
                <ChevronRight size={18} className="text-white/30" />
              </button>
              <button
                type="button"
                onClick={() => onShowAgreement?.()}
                className="w-full bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between text-left hover:bg-white/10 transition-colors"
              >
                <span className="text-sm font-medium text-white/80">用户服务协议</span>
                <ChevronRight size={18} className="text-white/30" />
              </button>
            </div>
          </div>

          {/* 账号安全 */}
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-white/40 mb-4">账号安全</h3>
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex justify-between items-center gap-4">
                  <div>
                    <span className="text-sm font-medium">修改密码</span>
                    <p className="text-[10px] text-white/40 mt-1">
                      需验证绑定手机号（内测验证码 {DEV_SMS_CODE}），新密码需输入两次
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowChangePwd((v) => !v)}
                    className="text-amber-500 text-sm font-bold hover:underline shrink-0"
                  >
                    {showChangePwd ? '收起' : '修改'}
                  </button>
                </div>
                {showChangePwd && (
                  <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
                    <p className="text-[10px] text-white/40">
                      验证码将发送至 {userProfile?.phone || '您的绑定手机'}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="6位验证码"
                        value={changeCode}
                        onChange={(e) => setChangeCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500/50"
                      />
                      <button
                        type="button"
                        disabled={codeTimer > 0}
                        onClick={handleSendChangeCode}
                        className="px-3 py-2.5 rounded-lg text-xs font-bold text-amber-500 border border-amber-500/30 disabled:opacity-40 whitespace-nowrap"
                      >
                        {codeTimer > 0 ? `${codeTimer}s` : '获取验证码'}
                      </button>
                    </div>
                    <input
                      type="password"
                      placeholder="新密码（至少6位）"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500/50"
                    />
                    <input
                      type="password"
                      placeholder="再次输入新密码"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500/50"
                    />
                    <button
                      type="button"
                      disabled={changingPwd}
                      onClick={handleChangePasswordSubmit}
                      className="w-full py-2.5 rounded-lg bg-amber-500 text-black text-sm font-black disabled:opacity-50"
                    >
                      {changingPwd ? '保存中…' : '确认修改密码'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <footer className="px-8 py-6 border-t border-white/5">
          <button onClick={handleUpdate} className="w-full py-4 bg-amber-500 text-black font-black rounded-xl hover:bg-amber-400 transition-colors shadow-lg shadow-amber-500/20">
            保存设置
          </button>
        </footer>
      </div>
    </div>
  );
};

// 作品互动详情（点赞 / 收藏 / 评论）
export const WorkInteractionsModal = ({ post, usersList = [], onClose, onViewUser }) => {
  const [tab, setTab] = useState('likes');
  const summary = buildWorkInteractionSummary(post, usersList);
  const tabs = [
    { id: 'likes', label: '点赞', count: summary.counts.likes },
    { id: 'bookmarks', label: '收藏', count: summary.counts.bookmarks },
    { id: 'comments', label: '评论', count: summary.counts.comments }
  ];

  const renderUserRow = (user, actionLabel) => (
    <button
      key={`${actionLabel}-${user.id || user.uid}`}
      type="button"
      onClick={() => onViewUser?.(user)}
      className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left"
    >
      <img src={user.avatar || DEFAULT_AVATAR} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold truncate">{user.name}</p>
        {user.uid && <p className="text-[10px] text-white/40 font-mono">ID: {user.uid}</p>}
      </div>
      <span className="text-[10px] text-amber-400 font-bold shrink-0">{actionLabel}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[5200] flex items-end md:items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-fade-in">
      <div className="bg-zinc-900 rounded-t-[2rem] md:rounded-[2rem] w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        <header className="px-6 py-5 border-b border-white/5 flex justify-between items-center gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-black text-amber-500 truncate">作品互动详情</h2>
            <p className="text-[10px] text-white/40 mt-1 truncate">{post?.title || post?.name || '我的作品'}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 bg-white/5 rounded-full hover:bg-white/10 shrink-0">
            <X size={22} />
          </button>
        </header>

        <div className="flex border-b border-white/5 px-2">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`flex-1 py-3 text-xs font-bold transition-colors relative ${
                tab === item.id ? 'text-amber-500' : 'text-white/40'
              }`}
            >
              {item.label} ({item.count})
              {tab === item.id && (
                <div className="absolute bottom-0 left-3 right-3 h-0.5 bg-amber-500 rounded-full" />
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-2">
          {tab === 'likes' && (
            summary.likers.length > 0
              ? summary.likers.map((user) => renderUserRow(user, '点赞'))
              : <div className="py-16 text-center opacity-40 text-sm">还没有人点赞</div>
          )}
          {tab === 'bookmarks' && (
            summary.bookmarkers.length > 0
              ? summary.bookmarkers.map((user) => renderUserRow(user, '收藏'))
              : <div className="py-16 text-center opacity-40 text-sm">还没有人收藏</div>
          )}
          {tab === 'comments' && (
            summary.comments.length > 0
              ? summary.comments.map((comment) => (
                  <button
                    key={comment.id}
                    type="button"
                    onClick={() => comment.actor && onViewUser?.(comment.actor)}
                    className="w-full flex gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left"
                  >
                    <img src={comment.userAvatar || comment.actor?.avatar || DEFAULT_AVATAR} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-sm font-bold truncate">{comment.userName || comment.actor?.name}</p>
                        <span className="text-[10px] text-white/40 shrink-0">
                          {comment.createdAt ? new Date(comment.createdAt).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <p className="text-sm text-white/75 break-words">{comment.content}</p>
                      {comment.actor?.uid && (
                        <p className="text-[10px] text-white/35 font-mono mt-1">ID: {comment.actor.uid}</p>
                      )}
                    </div>
                  </button>
                ))
              : <div className="py-16 text-center opacity-40 text-sm">还没有评论</div>
          )}
        </div>
      </div>
    </div>
  );
};

// 评论模态框
export const CommentModal = ({ post, onClose, onAddComment, showToast, userProfile }) => {
  const [comments, setComments] = useState(post.comments || []);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  const handleSubmitComment = async () => {
    if (commentSubmitting) return;
    if (!commentText.trim()) return showToast('请输入评论内容');
    if (!userProfile) return showToast('请先登录');

    const newComment = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      userId: userProfile.id,
      userName: userProfile.name,
      userAvatar: userProfile.avatar,
      content: commentText.trim(),
      createdAt: Date.now(),
      likes: 0
    };

    setCommentSubmitting(true);
    try {
      const syncResult = await onAddComment(post.id, newComment);
      setComments((prev) => [newComment, ...prev]);
      setCommentText('');
      if (syncResult === false) {
        showToast('评论成功，云端同步稍后完成');
        return;
      }
      showToast('评论成功！');
    } finally {
      setCommentSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-end justify-center bg-black/90 backdrop-blur-md p-4 animate-slide-up">
      <div className="bg-zinc-900 rounded-t-[2rem] w-full max-h-[80vh] flex flex-col shadow-2xl overflow-hidden">
        <header className="px-8 py-6 border-b border-white/5 flex justify-between items-center">
          <h2 className="text-xl font-black text-amber-500">评论</h2>
          <button onClick={onClose} className="p-2 bg-white/5 rounded-full hover:bg-white/10 transition-colors"><X size={24}/></button>
        </header>
        
        <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-6">
          {comments.length > 0 ? (
            comments.map((comment) => (
              <div key={comment.id} className="flex gap-4">
                <img src={comment.userAvatar || DEFAULT_AVATAR} className="w-10 h-10 rounded-full object-cover" alt="avatar"/>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-bold text-sm">{comment.userName}</h4>
                    <span className="text-xs text-white/40">{new Date(comment.createdAt).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-white/80 mb-2">{comment.content}</p>
                  <div className="flex items-center space-x-4">
                    <button className="text-xs text-white/40 hover:text-white flex items-center">
                      <Heart size={14} className="mr-1"/> {comment.likes || 0}
                    </button>
                    <button className="text-xs text-white/40 hover:text-white">回复</button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="py-20 flex flex-col items-center justify-center opacity-30">
              <MessageCircle size={48} className="mb-4"/>
              <p className="text-sm font-black uppercase">暂无评论</p>
              <p className="text-xs text-white/40 mt-2">快来发表第一条评论吧</p>
            </div>
          )}
        </div>
        
        <footer className="px-8 py-6 border-t border-white/5">
          <div className="flex items-center space-x-4">
            <img src={userProfile?.avatar || DEFAULT_AVATAR} className="w-10 h-10 rounded-full object-cover" alt="avatar"/>
            <div className="flex-1 relative">
              <input 
                type="text" 
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="写下你的评论..."
                className="w-full bg-white/5 border border-white/10 rounded-full py-3 px-5 pr-12 text-white outline-none focus:border-amber-500"
              />
              <button 
                type="button"
                onClick={handleSubmitComment}
                className={`absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${commentText.trim() && !commentSubmitting ? 'bg-amber-500 text-black' : 'bg-white/10 text-white/40'}`}
                disabled={!commentText.trim() || commentSubmitting}
              >
                {commentSubmitting ? '发送中…' : '发送'}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

/** 领养意向私信（需云库集合 adopt_messages，规则允许读写） */
export const AdoptChatModal = ({
  pet,
  userProfile,
  messages = [],
  threadId,
  threadChoices = [],
  onClose,
  onSend,
  onPickThread
}) => {
  const [text, setText] = useState('');
  const ownerId = String(pet?.userId ?? pet?.uid ?? pet?.phone ?? '');
  const selfId = String(userProfile?.id ?? userProfile?.uid ?? userProfile?.phone ?? '');
  const isOwner = ownerId && selfId && ownerId === selfId;

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    await onSend(t);
    setText('');
  };

  const showThreadPicker = isOwner && !threadId && threadChoices.length > 0;

  return (
    <div className="fixed inset-0 z-[4900] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-zinc-900 border border-white/10 rounded-[2rem] w-full max-w-md h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg font-black text-amber-500 truncate">{pet?.name || '领养沟通'}</h3>
            <p className="text-[10px] text-white/40 truncate">发布者 @{pet?.user || '用户'}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full bg-white/5 hover:bg-white/10">
            <X size={22} />
          </button>
        </header>
        <p className="px-4 py-2 text-[10px] text-amber-200/80 bg-amber-500/10 border-b border-amber-500/20 leading-relaxed">
          私下沟通请注意人身与财产安全，谨防诈骗；达成一致后请通过正规流程完成领养。
        </p>

        {showThreadPicker && (
          <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
            <p className="text-xs text-white/50 mb-2">选择一条咨询会话：</p>
            {threadChoices.map((c) => (
              <button
                key={c.threadId}
                type="button"
                onClick={() => onPickThread(c.threadId)}
                className="w-full text-left p-4 rounded-xl bg-white/5 border border-white/10 hover:border-amber-500/40 transition-colors"
              >
                <p className="text-xs font-bold text-white/80 line-clamp-1">{c.snippet || '（无文字）'}</p>
                <p className="text-[10px] text-white/30 mt-1">{new Date(c.lastAt || 0).toLocaleString()}</p>
              </button>
            ))}
          </div>
        )}

        {isOwner && !threadId && threadChoices.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center text-white/40 text-sm px-8 text-center">
            <MessageCircle size={40} className="mb-3 opacity-50" />
            <p>暂无用户留言。当有领养意向人发起沟通时，会话会出现在这里。</p>
          </div>
        )}

        {!!threadId && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
              {messages.length === 0 ? (
                <p className="text-center text-white/30 text-sm py-12">还没有消息，打个招呼吧～</p>
              ) : (
                messages.map((m) => {
                  const mine = String(m.fromUserId) === selfId;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                          mine ? 'bg-amber-500 text-black' : 'bg-white/10 text-white'
                        }`}
                      >
                        {!mine && <p className="text-[10px] font-bold text-amber-400 mb-1">{m.fromName || '用户'}</p>}
                        <p className="leading-relaxed whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[9px] mt-1 ${mine ? 'text-black/50' : 'text-white/30'}`}>
                          {new Date(m.createdAt || 0).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <footer className="p-4 border-t border-white/10 shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="输入消息…"
                  rows={2}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-amber-500 resize-none"
                />
                <button
                  type="button"
                  onClick={submit}
                  className="p-3 rounded-xl bg-amber-500 text-black hover:bg-amber-400 transition-colors"
                >
                  <Send size={18} />
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export const SearchModal = ({ onClose, posts, users, onGoToProfile, showToast }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // 收集所有可能的搜索关键词
  const collectKeywords = () => {
    const keywords = new Set();
    
    // 从用户中收集关键词
    users.forEach(user => {
      if (user.name) keywords.add(user.name);
      if (user.uid) keywords.add(user.uid);
    });
    
    // 从帖子中收集关键词
    posts.forEach(post => {
      if (post.title) keywords.add(post.title);
      if (post.content) {
        // 从内容中提取关键词
        const contentWords = post.content.split(/\s+/);
        contentWords.forEach(word => {
          if (word.length > 2) keywords.add(word);
        });
      }
      if (Array.isArray(post.tags)) {
        post.tags.forEach(tag => keywords.add(tag));
      }
    });
    
    return Array.from(keywords);
  };
  
  // 处理搜索输入变化
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    if (value.trim()) {
      const allKeywords = collectKeywords();
      const filtered = allKeywords.filter(keyword => 
        keyword.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5); // 只显示前5个建议
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };
  
  // 选择建议
  const selectSuggestion = (suggestion) => {
    setQuery(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
  };
  
  const filteredPosts = query && posts.length > 0 ? posts.filter(p => {
    const titleMatch = p.title?.toLowerCase().includes(query.toLowerCase());
    const contentMatch = p.content?.toLowerCase().includes(query.toLowerCase());
    const tagsMatch = Array.isArray(p.tags) && p.tags.some(t => t.toLowerCase().includes(query.toLowerCase()));
    return titleMatch || contentMatch || tagsMatch;
  }) : [];
  
  const filteredUsers = query && users.length > 0 ? users.filter(u => {
    const nameMatch = u.name?.toLowerCase().includes(query.toLowerCase());
    const uidMatch = u.uid?.toLowerCase().includes(query.toLowerCase());
    return nameMatch || uidMatch;
  }) : [];

  return (
    <div className="fixed inset-0 z-[3000] bg-stone-950 flex flex-col animate-fade-in">
      <header className="px-6 py-4 border-b border-white/5 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={onClose} className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-full transition-colors"><ChevronLeft size={24}/></button>
        <div className="relative flex-1">
          <input 
            autoFocus 
            type="text" 
            placeholder="搜索用户/作品/标签" 
            value={query} 
            onChange={handleInputChange}
            onFocus={() => query && setShowSuggestions(true)}
            className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-3 text-sm text-white outline-none focus:border-amber-500" 
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-white/10 rounded-xl shadow-2xl z-50">
              {suggestions.map((suggestion, index) => (
                <div 
                  key={index} 
                  onClick={() => selectSuggestion(suggestion)}
                  className="px-4 py-3 text-sm hover:bg-white/5 cursor-pointer"
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto no-scrollbar p-6">
        {query ? (
          <>
            {filteredUsers.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-black text-white/40 mb-4 uppercase">用户</h3>
                <div className="space-y-3">
                  {filteredUsers.map(user => (
                    <div key={user.id} onClick={() => { onGoToProfile(user.id); onClose(); }} className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 cursor-pointer">
                      <img src={user.avatar || DEFAULT_AVATAR} className="w-12 h-12 rounded-full" alt="avatar" />
                      <div>
                        <p className="font-bold">{user.name} {user.role === 'super_admin' && <BadgeCheck size={14} className="text-amber-500 ml-1"/>}</p>
                        <p className="text-xs text-white/40">UID: {user.uid}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {filteredPosts.length > 0 && (
              <div>
                <h3 className="text-sm font-black text-white/40 mb-4 uppercase">作品</h3>
                <div className="grid grid-cols-3 gap-3">
                  {filteredPosts.map(post => (
                    <div key={post.id} className="aspect-square rounded-xl overflow-hidden relative">
                      <img src={post.img} className="w-full h-full object-cover" alt="post" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {filteredPosts.length === 0 && filteredUsers.length === 0 && (
              <div className="py-20 text-center opacity-30">
                <p className="font-black">暂无搜索结果</p>
              </div>
            )}
          </>
        ) : (
          <div className="py-20 text-center opacity-30">
            <Search size={48} className="mx-auto mb-4"/>
            <p className="font-black">输入关键词搜索</p>
          </div>
        )}
      </div>
    </div>
  );
};

